import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, Menu as MenuIcon, X as XIcon } from 'lucide-react';
import SkillDeckLogo from '../components/SkillDeckLogo';
import AgentDemo from '../components/AgentDemo';

// Avant / après, sur les quatre gestes qui occupent vraiment la journée d'un BM.
// La colonne « avant » décrit le travail réel, pas une caricature : c'est ce qui
// rend la colonne « après » crédible.
const METHOD_ROWS = [
  {
    task: 'Créer un dossier de compétences',
    oldLead: '30 – 45 min',
    oldDetail: 'de copier-coller entre le CV et Word',
    newLead: '2 min',
    newDetail: 'uploadez, personnalisez, exportez',
  },
  {
    task: 'Trouver le bon consultant',
    oldLead: '5 outils',
    oldDetail: 'à ouvrir, et les disponibilités à croiser à la main',
    newLead: '1 phrase',
    newDetail: '« Trouve un dev Java senior dispo en janvier »',
  },
  {
    task: 'Suivre les missions actives',
    oldLead: 'Excel + emails',
    oldDetail: 'et une réunion de synchro pour recoller les morceaux',
    newLead: 'Temps réel',
    newDetail: 'un tableau de bord synchronisé automatiquement',
  },
  {
    task: "Répondre à un appel d'offres",
    oldLead: '2 jours',
    oldDetail: 'pour compiler les CV et les références',
    newLead: '2 heures',
    newDetail: 'avec génération automatique du dossier de réponse',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});

  const rootRef = useRef(null);
  const mountedRef = useRef(true);

  // Open login panel if redirected from /login
  useEffect(() => {
    if (location.state?.openLogin) setShowLogin(true);
  }, [location.state]);

  // Lock body scroll when panel or mobile menu is open
  useEffect(() => {
    document.body.style.overflow = (showLogin || mobileMenuOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showLogin, mobileMenuOpen]);

  // Title
  useEffect(() => {
    document.title = 'SkillDeck – Le copilot sales des business managers en ESN';
  }, []);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle(); // redirects to Google; returns to /dashboard
    } catch (error) {
      toast({
        title: 'Connexion Google indisponible',
        description: error?.message || "Le fournisseur Google n'est pas encore configuré.",
        variant: 'destructive',
      });
      setGoogleLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!loginEmail.trim()) errs.email = "L'email est requis";
    else if (!/\S+@\S+\.\S+/.test(loginEmail)) errs.email = 'Email invalide';
    if (!loginPassword) errs.password = 'Le mot de passe est requis';
    setLoginErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoginLoading(true);
    try {
      const result = await login(loginEmail, loginPassword);
      if (result.success) {
        toast({ title: 'Connexion réussie', description: 'Bienvenue sur Skilldeck' });
        navigate('/dashboard');
      }
    } catch (error) {
      const msg = error?.message || '';
      let description = 'Email ou mot de passe incorrect';
      if (msg.toLowerCase().includes('email not confirmed')) {
        description = 'Email non confirmé. Vérifiez votre boîte mail.';
      } else if (msg.toLowerCase().includes('invalid login')) {
        description = 'Email ou mot de passe incorrect.';
      } else if (msg) {
        description = msg;
      }
      toast({ title: 'Erreur de connexion', description, variant: 'destructive' });
    } finally {
      setLoginLoading(false);
    }
  };

  const goRegister = () => navigate('/register');

  // ── Scroll fade-up + section-specific animations ──
  useEffect(() => {
    mountedRef.current = true;
    const root = rootRef.current;
    if (!root) return;

    // Scroll fade-up
    const fadeObserver = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );
    root.querySelectorAll('.fade-up').forEach((el) => fadeObserver.observe(el));
    // Trigger hero immediately for above-the-fold elements
    root.querySelectorAll('.fade-up').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) el.classList.add('visible');
    });

    // ── Hero App UI Animation ──
    const heroTimeouts = [];
    const heroAdd = (id, val) => {
      const el = root.querySelector('#' + id);
      if (!el) return;
      val ? el.classList.add('show') : el.classList.remove('show');
    };
    const resetHero = () => {
      ['auiUpload', 'auiStatus', 'auiProfile', 'auiDC'].forEach((id) => heroAdd(id, false));
      root.querySelectorAll('.aui-tag').forEach((t) => t.classList.remove('show'));
      root.querySelectorAll('.aui-bar-fill').forEach((b) => { b.style.transition = 'none'; b.style.width = '0%'; });
      root.querySelectorAll('.aui-btn-export').forEach((b) => b.classList.remove('show'));
      heroAdd('auiGenTime', false);
      const dot = root.querySelector('#auiDot');
      if (dot) dot.classList.remove('done');
      const txt = root.querySelector('#auiStatusText');
      if (txt) txt.textContent = 'Analyse IA en cours…';
      const time = root.querySelector('#auiTime');
      if (time) time.textContent = '';
    };
    const runHero = () => {
      if (!mountedRef.current) return;
      resetHero();
      heroTimeouts.push(setTimeout(() => heroAdd('auiUpload', true), 200));
      heroTimeouts.push(setTimeout(() => heroAdd('auiStatus', true), 550));
      heroTimeouts.push(setTimeout(() => heroAdd('auiProfile', true), 1700));
      const tags = root.querySelectorAll('.aui-tag');
      tags.forEach((tag, i) => {
        heroTimeouts.push(setTimeout(() => tag.classList.add('show'), 2100 + i * 110));
      });
      heroTimeouts.push(setTimeout(() => {
        const dot = root.querySelector('#auiDot');
        if (dot) dot.classList.add('done');
        const txt = root.querySelector('#auiStatusText');
        if (txt) txt.textContent = 'Analyse IA terminée';
        const time = root.querySelector('#auiTime');
        if (time) time.textContent = '· 00:48';
      }, 2900));
      heroTimeouts.push(setTimeout(() => heroAdd('auiDC', true), 3300));
      heroTimeouts.push(setTimeout(() => {
        const bars = root.querySelectorAll('.aui-bar-fill');
        bars.forEach((bar, i) => {
          heroTimeouts.push(setTimeout(() => {
            bar.style.transition = 'width 1.1s cubic-bezier(.4,0,.2,1)';
            bar.style.width = bar.dataset.w + '%';
          }, i * 280));
        });
      }, 3650));
      const btns = root.querySelectorAll('.aui-btn-export');
      btns.forEach((btn, i) => {
        heroTimeouts.push(setTimeout(() => btn.classList.add('show'), 4750 + i * 130));
      });
      heroTimeouts.push(setTimeout(() => heroAdd('auiGenTime', true), 5300));
      heroTimeouts.push(setTimeout(runHero, 8500));
    };
    heroTimeouts.push(setTimeout(runHero, 700));

    // ── AI Matching animation ──
    const matchTimeouts = [];
    let matchRunning = false;
    let matchStarted = false;
    const SKILLS = ['React', 'Node.js', 'AWS', 'Python', 'Docker'];
    const MATCHES = { React: true, 'Node.js': true, AWS: true, Python: false, Docker: true };
    const resetMatch = () => {
      SKILLS.forEach((s) => {
        const sk = root.querySelector(`.mc-skill[data-skill="${s}"]`);
        if (sk) sk.className = 'mc-skill';
        const rq = root.querySelector(`.mc-req[data-req="${s}"]`);
        if (rq) {
          rq.className = 'mc-req';
          const ic = rq.querySelector('.mc-req-icon');
          if (ic) ic.textContent = '○';
        }
      });
      const sc = root.querySelector('#matchScore');
      if (sc) sc.textContent = '—';
      const res = root.querySelector('#matchResult');
      if (res) res.classList.remove('show');
    };
    const countUp = (el, target, ms) => {
      const t0 = performance.now();
      const tick = (t) => {
        if (!mountedRef.current) return;
        const p = Math.min((t - t0) / ms, 1);
        el.textContent = Math.round(p * target) + '%';
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const runMatch = () => {
      if (matchRunning || !mountedRef.current) return;
      matchRunning = true;
      resetMatch();
      SKILLS.forEach((s, i) => {
        matchTimeouts.push(setTimeout(() => {
          const sk = root.querySelector(`.mc-skill[data-skill="${s}"]`);
          if (sk) sk.classList.add(MATCHES[s] ? 'sm' : 'sn');
          matchTimeouts.push(setTimeout(() => {
            const rq = root.querySelector(`.mc-req[data-req="${s}"]`);
            if (rq) {
              rq.classList.add(MATCHES[s] ? 'rm' : 'rn');
              const ic = rq.querySelector('.mc-req-icon');
              if (ic) ic.textContent = MATCHES[s] ? '✓' : '✗';
            }
            if (i === SKILLS.length - 1) {
              matchTimeouts.push(setTimeout(() => {
                const sc = root.querySelector('#matchScore');
                if (sc) countUp(sc, 94, 900);
                matchTimeouts.push(setTimeout(() => {
                  const res = root.querySelector('#matchResult');
                  if (res) res.classList.add('show');
                  matchTimeouts.push(setTimeout(() => { matchRunning = false; runMatch(); }, 3400));
                }, 960));
              }, 300));
            }
          }, 500));
        }, 600 + i * 700));
      });
    };
    const matchSection = root.querySelector('#matching');
    const matchObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !matchStarted) {
          matchStarted = true;
          matchTimeouts.push(setTimeout(runMatch, 500));
        }
      },
      { threshold: 0.25 }
    );
    if (matchSection) matchObserver.observe(matchSection);

    // ── Library mockup animation ──
    const libTimeouts = [];
    let libAnimated = false;
    let libCanceled = false;
    const SEARCH_QUERIES = ['React Senior', 'Data Engineer', 'DevOps Cloud', 'Thomas Mer'];
    let qIdx = 0;
    const typeSearch = (query, el, done) => {
      let i = 0;
      el.innerHTML = '<span class="lib-search-cursor"></span>';
      const iv = setInterval(() => {
        if (!mountedRef.current || libCanceled) { clearInterval(iv); return; }
        el.innerHTML = query.slice(0, i) + '<span class="lib-search-cursor"></span>';
        i++;
        if (i > query.length) {
          clearInterval(iv);
          libTimeouts.push(setTimeout(done, 1400));
        }
      }, 70);
    };
    const showLibCards = () => {
      ['lc0', 'lc1', 'lc2', 'lc3', 'lc4', 'lc5'].forEach((id, i) => {
        libTimeouts.push(setTimeout(() => {
          const el = root.querySelector('#' + id);
          if (el) el.classList.add('show');
        }, i * 90));
      });
    };
    const cycleSearch = () => {
      if (!mountedRef.current || libCanceled) return;
      const txt = root.querySelector('#libSearchText');
      if (!txt) return;
      qIdx = (qIdx + 1) % SEARCH_QUERIES.length;
      typeSearch(SEARCH_QUERIES[qIdx], txt, () => {
        ['libChipAll', 'libChipDispo', 'libChipReact', 'libChipData', 'libChipCloud'].forEach((id) => {
          const el = root.querySelector('#' + id);
          if (el) el.classList.remove('active');
        });
        const chips = ['libChipAll', 'libChipDispo', 'libChipReact', 'libChipData', 'libChipCloud'];
        const active = chips[qIdx % chips.length];
        const activEl = root.querySelector('#' + active);
        if (activEl) activEl.classList.add('active');
        txt.innerHTML = '<span class="lib-search-cursor"></span>';
        libTimeouts.push(setTimeout(cycleSearch, 2200));
      });
    };
    const libSection = root.querySelector('#library');
    const libObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !libAnimated) {
          libAnimated = true;
          showLibCards();
          libTimeouts.push(setTimeout(cycleSearch, 1200));
        }
      },
      { threshold: 0.25 }
    );
    if (libSection) libObserver.observe(libSection);

    return () => {
      mountedRef.current = false;
      libCanceled = true;
      fadeObserver.disconnect();
      matchObserver.disconnect();
      libObserver.disconnect();
      heroTimeouts.forEach((t) => clearTimeout(t));
      matchTimeouts.forEach((t) => clearTimeout(t));
      libTimeouts.forEach((t) => clearTimeout(t));
    };
  }, []);

  return (
    <div className="sd-v2" ref={rootRef}>
      <style>{landingCSS}</style>

      {/* NAV */}
      <nav>
        <a href="#" className="nav-logo" onClick={(e) => e.preventDefault()}>
          <SkillDeckLogo size={32} />
          <span className="nav-logo-text">Skill<span>Deck</span></span>
        </a>
        <ul className="nav-links">
          {/* Méga-menu produit — ouvert au survol et au clavier, pour donner à
              voir tout le produit sans avoir à parcourir la page. */}
          <li
            className={`nav-mega-wrap${megaOpen ? ' open' : ''}`}
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
          >
            <button
              className="nav-mega-trigger"
              aria-expanded={megaOpen}
              aria-haspopup="true"
              onClick={() => setMegaOpen((o) => !o)}
            >
              Produit
              <svg className="nav-mega-caret" width="11" height="11" viewBox="0 0 12 12" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 4.5 6 7.5 9 4.5" />
              </svg>
            </button>

            <div className="nav-mega" onClick={() => setMegaOpen(false)}>
              <div className="nav-mega-col">
                <div className="nav-mega-head">Piloter par l'agent</div>
                <a href="#agent">
                  <span className="nmi-t">Agent<span className="nmi-badge">Nouveau</span></span>
                  <span className="nmi-d">Dites ce que vous voulez, il l'exécute</span>
                </a>
                <div className="nav-mega-head" style={{ marginTop: 14 }}>Générer</div>
                <a href="#features">
                  <span className="nmi-t">Dossiers de compétences</span>
                  <span className="nmi-d">D'un CV à un dossier prêt à envoyer en 2 min</span>
                </a>
                <a href="#library">
                  <span className="nmi-t">Bibliothèque de DC</span>
                  <span className="nmi-d">Tous vos consultants, cherchables en un mot</span>
                </a>
                <a href="#features">
                  <span className="nmi-t">Templates & branding</span>
                  <span className="nmi-d">Vos couleurs, votre logo, votre identité</span>
                </a>
              </div>

              <div className="nav-mega-col">
                <div className="nav-mega-head">Placer</div>
                <a href="#matching">
                  <span className="nmi-t">Matching IA</span>
                  <span className="nmi-d">Le bon profil pour chaque appel d'offres</span>
                </a>
                <a href="#crm">
                  <span className="nmi-t">Appels d'offres</span>
                  <span className="nmi-d">Import, analyse et scoring automatiques</span>
                </a>
                <a href="#crm">
                  <span className="nmi-t">Shortlists</span>
                  <span className="nmi-d">Une sélection prête à envoyer au client</span>
                </a>
              </div>

              <div className="nav-mega-col">
                <div className="nav-mega-head">Piloter</div>
                <a href="#crm">
                  <span className="nmi-t">CRM client<span className="nmi-badge">Nouveau</span></span>
                  <span className="nmi-d">Comptes, entités, équipes et interlocuteurs</span>
                </a>
                <a href="#crm">
                  <span className="nmi-t">Relances</span>
                  <span className="nmi-d">Les clients qui passent sous le radar</span>
                </a>
                <a href="#crm">
                  <span className="nmi-t">Tableau de bord</span>
                  <span className="nmi-d">Vos actions du jour, priorisées</span>
                </a>
              </div>

              <div className="nav-mega-foot">
                <a href="#usecases">Voir tous les cas d'usage →</a>
              </div>
            </div>
          </li>
          <li><a href="#how">Comment ça marche</a></li>
          <li><a href="#usecases">Cas d'usage</a></li>
          <li><a href="#pricing">Tarifs</a></li>
        </ul>
        {/* La mise en forme vit dans le CSS : un `display: flex` inline
            écrasait la règle qui masque ces boutons sous 1024px, et ils
            débordaient sur le logo. */}
        <div className="nav-actions">
          <button className="btn btn-ghost" onClick={() => setShowLogin(true)}>
            Se connecter
          </button>
          <button className="btn btn-primary" onClick={goRegister}>
            Essayer gratuitement →
          </button>
        </div>
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
        </button>
      </nav>

      {/* MOBILE MENU */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          {/* Sur mobile, le méga-menu s'aplatit en listes titrées : un survol
              n'existe pas au doigt, et un accordéon de plus rallongerait le
              chemin vers l'information. */}
          <ul className="mobile-menu-links">
            <li className="mm-head">Générer</li>
            <li><a href="#features" onClick={() => setMobileMenuOpen(false)}>Dossiers de compétences</a></li>
            <li><a href="#library" onClick={() => setMobileMenuOpen(false)}>Bibliothèque de DC</a></li>
            <li className="mm-head">Placer</li>
            <li><a href="#agent" onClick={() => setMobileMenuOpen(false)}>Agent</a></li>
            <li><a href="#matching" onClick={() => setMobileMenuOpen(false)}>Matching IA</a></li>
            <li className="mm-head">Piloter</li>
            <li><a href="#crm" onClick={() => setMobileMenuOpen(false)}>CRM client</a></li>
            <li className="mm-sep" />
            <li><a href="#how" onClick={() => setMobileMenuOpen(false)}>Comment ça marche</a></li>
            <li><a href="#usecases" onClick={() => setMobileMenuOpen(false)}>Cas d'usage</a></li>
            <li><a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Tarifs</a></li>
          </ul>
          <div className="mobile-menu-actions">
            <button
              className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => { setMobileMenuOpen(false); setShowLogin(true); }}
            >
              Se connecter
            </button>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => { setMobileMenuOpen(false); goRegister(); }}
            >
              Essayer gratuitement →
            </button>
          </div>
        </div>
      )}

      {/* HERO */}
      <section id="hero">
        <div className="aurora-bg">
          <div className="aurora-blob ab1"></div>
          <div className="aurora-blob ab2"></div>
          <div className="aurora-blob ab3"></div>
        </div>
        <div className="hero-grid"></div>

        <div className="container">
          <div className="hero-inner">
            <div className="hero-content fade-up">
              <a className="hero-eyebrow hero-eyebrow-link" href="#agent">
                Nouveau · Agent SkillDeck — voir la démo →
              </a>

              <h1 className="hero-title">
                En ESN, le plus réactif<br />
                <span className="grad">remporte le deal.</span>
              </h1>

              <p className="hero-sub">
                SkillDeck est le copilot sales qui fait de votre réactivité une arme :
                matchez votre vivier aux appels d'offres et positionnez le bon consultant
                avant la concurrence.
              </p>

              <div className="hero-ctas">
                <button className="btn btn-primary btn-lg" onClick={goRegister}>
                  Essayer gratuitement →
                </button>
                <a href="#cta" className="btn btn-ghost btn-lg">Voir une démo</a>
              </div>

              <div className="hero-trust">
                <div className="hero-trust-avatars">
                  <div className="av">JD</div>
                  <div className="av">ML</div>
                  <div className="av">AB</div>
                  <div className="av">RC</div>
                </div>
                <div className="hero-trust-text">
                  <strong>Des centaines de business managers</strong> placent plus vite avec SkillDeck
                </div>
              </div>
            </div>

            {/* Mockup — DC animé */}
            <div className="hero-mockup fade-up" style={{ animationDelay: '.15s' }}>
              <div className="hero-glow"></div>
              <div className="mockup-shell">
                <div className="mockup-titlebar">
                  <div className="traffic-lights">
                    <div className="tl tl-r"></div>
                    <div className="tl tl-y"></div>
                    <div className="tl tl-g"></div>
                  </div>
                  <div className="mockup-url">app.skilldeck.io</div>
                </div>
                <div className="mockup-body">
                  <div className="app-ui" id="appUI">
                    {/* Upload zone */}
                    <div className="aui-upload" id="auiUpload">
                      <div className="aui-upload-icon">📄</div>
                      <div>
                        <span className="aui-filename">Thomas_Mercier_CV.pdf</span>
                        <span className="aui-upload-ok"> — chargé avec succès</span>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="aui-status" id="auiStatus">
                      <span className="aui-status-dot" id="auiDot"></span>
                      <span className="aui-status-text" id="auiStatusText">Analyse IA en cours…</span>
                      <span className="aui-status-time" id="auiTime"></span>
                    </div>

                    {/* Profile card */}
                    <div className="aui-profile" id="auiProfile">
                      <div className="aui-profile-top">
                        <div className="aui-avatar">TM</div>
                        <div>
                          <div className="aui-name">Thomas Mercier</div>
                          <div className="aui-role-text">Lead Dev Full Stack · 7 ans d'expérience</div>
                        </div>
                      </div>
                      <div className="aui-tags" id="auiTagsWrap">
                        <span className="aui-tag">React</span>
                        <span className="aui-tag">Node.js</span>
                        <span className="aui-tag">AWS</span>
                        <span className="aui-tag">PostgreSQL</span>
                        <span className="aui-tag">DevOps</span>
                      </div>
                    </div>

                    {/* DC Generation */}
                    <div className="aui-dc" id="auiDC">
                      <div className="aui-dc-header">↓ Dossier de Compétences</div>
                      <div className="aui-bars">
                        <div className="aui-bar-track"><div className="aui-bar-fill" data-w="100"></div></div>
                        <div className="aui-bar-track"><div className="aui-bar-fill" data-w="72"></div></div>
                        <div className="aui-bar-track"><div className="aui-bar-fill" data-w="52"></div></div>
                      </div>
                      <div className="aui-actions">
                        <div className="aui-exports">
                          <button className="aui-btn-export" id="auiBtnPDF">PDF</button>
                          <button className="aui-btn-export" id="auiBtnPPT">PPT</button>
                          <button className="aui-btn-export aui-btn-link" id="auiBtnLink">🔗 Lien</button>
                        </div>
                        <span className="aui-gen-time" id="auiGenTime">Généré en 48s</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section id="stats">
        <div className="container">
          <div className="stats-inner">
            <div className="stat fade-up">
              <div className="stat-num">&lt;&nbsp;2 min</div>
              <div className="stat-label">Pour positionner un consultant sur un besoin</div>
            </div>
            <div className="stat fade-up">
              <div className="stat-num">10 000+</div>
              <div className="stat-label">Consultants dans les viviers SkillDeck</div>
            </div>
            <div className="stat fade-up">
              <div className="stat-num">98%</div>
              <div className="stat-label">Recommandent SkillDeck à leurs équipes</div>
            </div>
          </div>
        </div>
      </section>

      {/* AGENT — démo interactive */}
      <section id="agent">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center fade-up">
            <div className="section-eyebrow">Nouveau · Agent</div>
            <h2 className="section-title">
              Dites ce que vous voulez.<br />
              <span className="grad">L'agent le fait.</span>
            </h2>
            <p className="section-sub">
              Pas un chatbot qui répond : un agent qui exécute dans SkillDeck. Il crée le besoin,
              cherche dans votre vivier, rédige les emails — et s'arrête avant d'envoyer quoi que ce soit.
            </p>
          </div>

          <div className="fade-up">
            <AgentDemo />
          </div>

          <div className="agent-pills fade-up">
            <div className="agent-pill">⚡ Plusieurs actions en une phrase</div>
            <div className="agent-pill">🔒 Aucun envoi sans votre validation</div>
            <div className="agent-pill">🇪🇺 IA Mistral, données en Europe</div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="problem">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="fade-up">
            <div className="section-eyebrow left">Le problème</div>
            <h2 className="section-title">
              Rater un placement,<br />
              <span className="grad">c'est un manque à gagner.</span>
            </h2>
            <p className="section-sub">
              Dans une ESN, chaque jour de retard sur un besoin client, c'est une mission qui part
              chez le concurrent. SkillDeck accélère tout votre cycle de placement.
            </p>
          </div>
          <div className="problem-grid">
            <div className="problem-card fade-up">
              <div className="problem-icon">⏱️</div>
              <h3>Le bon profil, trop tard</h3>
              <p>Le temps de retrouver le CV, de le formater et de l'envoyer, le client a déjà reçu trois autres profils. En placement, la réactivité fait la vente.</p>
            </div>
            <div className="problem-card fade-up">
              <div className="problem-icon">🗂️</div>
              <h3>Un vivier sous-exploité</h3>
              <p>Vos meilleurs consultants dorment dans des dossiers éparpillés. Sans vision claire du vivier, vous passez à côté des matchs évidents entre profils et besoins.</p>
            </div>
            <div className="problem-card fade-up">
              <div className="problem-icon">🐢</div>
              <h3>Cycles de vente ralentis</h3>
              <p>Un client attend un profil en 2h. Entre le sourcing, le dossier et la relance, chaque étape manuelle vous coûte des marchés face à un concurrent plus réactif.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AVANT / APRÈS */}
      <section id="methode">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center fade-up">
            <div className="section-eyebrow">Pourquoi ils adoptent SkillDeck</div>
            <h2 className="section-title">
              Le même métier,<br />
              <span className="grad">sans le travail perdu.</span>
            </h2>
            <p className="section-sub">
              Rien ne change à votre façon de vendre. Ce qui change, c'est le temps
              que vous passiez à faire ce qu'une machine fait mieux.
            </p>
          </div>

          <div className="ba fade-up">
            {/* Même grille que les lignes, pour que les libellés tombent
                exactement au-dessus de leur colonne. */}
            <div className="ba-legend">
              <span />
              <span className="ba-legend-old">Ancienne méthode</span>
              <span />
              <span className="ba-legend-new">Nouvelle méthode</span>
            </div>

            {METHOD_ROWS.map((row) => (
              <div className="ba-row" key={row.task}>
                <div className="ba-task">{row.task}</div>
                <div className="ba-cell ba-old">
                  <span className="ba-lead">{row.oldLead}</span>
                  <span className="ba-detail">{row.oldDetail}</span>
                </div>
                <div className="ba-arrow" aria-hidden="true">→</div>
                <div className="ba-cell ba-new">
                  <span className="ba-lead">{row.newLead}</span>
                  <span className="ba-detail">{row.newDetail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features">
        <div className="container">
          <div className="text-center fade-up">
            <div className="section-eyebrow">Fonctionnalités</div>
            <h2 className="section-title">
              Tout ce dont vous avez besoin,<br />
              <span className="grad">sans la complexité.</span>
            </h2>
            <p className="section-sub">
              De la captation du besoin à l'envoi du dossier, SkillDeck outille
              chaque étape de votre placement.
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-card fade-up">
              <div className="feature-icon">📤</div>
              <h3>Upload universel</h3>
              <p>Importez n'importe quel CV en PDF ou Word. Notre IA analyse et comprend la structure, quelle que soit sa mise en page.</p>
            </div>
            <div className="feature-card fade-up">
              <div className="feature-icon">🤖</div>
              <h3>Extraction IA</h3>
              <p>Compétences, stack technique, expériences, missions — tout est extrait, catégorisé et hiérarchisé automatiquement.</p>
            </div>
            <div className="feature-card fade-up">
              <div className="feature-icon">🎨</div>
              <h3>20 templates pro</h3>
              <p>Choisissez parmi 20 templates professionnels. Ajoutez votre logo, vos couleurs, votre identité visuelle.</p>
            </div>
            <div className="feature-card fade-up">
              <div className="feature-icon">📦</div>
              <h3>Export multi-format</h3>
              <p>Exportez en PDF, PowerPoint ou partagez via un lien sécurisé directement avec vos clients, en un clic.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI MATCHING */}
      <section id="matching">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center fade-up">
            <div className="section-eyebrow">Nouvelle fonctionnalité · Bêta</div>
            <h2 className="section-title">
              IA Matching<br />
              <span className="grad">Le bon profil, au bon moment.</span>
            </h2>
            <p className="section-sub">
              SkillDeck analyse automatiquement la compatibilité entre vos dossiers de compétences
              et vos appels d'offres. Score de matching en quelques secondes.
            </p>
          </div>

          <div className="match-scene fade-up">
            {/* Left: Profile */}
            <div className="match-card left-card">
              <div className="mc-label">Dossier de compétences</div>
              <div className="mc-head">
                <div className="mc-av">TM</div>
                <div>
                  <div className="mc-name">Thomas M.</div>
                  <div className="mc-sub">Lead Dev · 7 ans exp.</div>
                </div>
              </div>
              <div className="mc-skills">
                <div className="mc-skill" data-skill="React">React</div>
                <div className="mc-skill" data-skill="Node.js">Node.js</div>
                <div className="mc-skill" data-skill="AWS">AWS</div>
                <div className="mc-skill" data-skill="Python">Python</div>
                <div className="mc-skill" data-skill="Docker">Docker</div>
              </div>
            </div>

            {/* Center: AI Engine */}
            <div className="match-engine">
              <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                <defs>
                  <radialGradient id="cg" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#6c47ff" stopOpacity=".5" />
                    <stop offset="100%" stopColor="#6c47ff" stopOpacity=".08" />
                  </radialGradient>
                  <filter id="eg">
                    <feGaussianBlur stdDeviation="3" result="b" />
                    <feComposite in="SourceGraphic" in2="b" operator="over" />
                  </filter>
                </defs>

                <g>
                  <animateTransform attributeName="transform" type="rotate" from="0 100 100" to="360 100 100" dur="28s" repeatCount="indefinite" />
                  <circle cx="100" cy="100" r="92" fill="none" stroke="rgba(108,71,255,.1)" strokeWidth="1" strokeDasharray="6 5" />
                  <circle cx="100" cy="8" r="4.5" fill="rgba(108,71,255,.5)" filter="url(#eg)" />
                  <circle cx="192" cy="100" r="3" fill="rgba(96,165,250,.4)" />
                  <circle cx="100" cy="192" r="3" fill="rgba(167,139,250,.35)" />
                </g>

                <g>
                  <animateTransform attributeName="transform" type="rotate" from="360 100 100" to="0 100 100" dur="17s" repeatCount="indefinite" />
                  <circle cx="100" cy="100" r="68" fill="none" stroke="rgba(108,71,255,.2)" strokeWidth="1.5" strokeDasharray="4 7" />
                  <circle cx="100" cy="32" r="3.5" fill="rgba(167,139,250,.55)" filter="url(#eg)" />
                  <circle cx="168" cy="100" r="3" fill="rgba(96,165,250,.45)" />
                  <circle cx="32" cy="100" r="2.5" fill="rgba(108,71,255,.5)" />
                </g>

                <circle cx="100" cy="100" r="46" fill="url(#cg)" stroke="rgba(108,71,255,.35)" strokeWidth="1.5" />

                <circle cx="100" cy="54" r="3.5" fill="#a78bfa" opacity=".75" />
                <circle cx="140" cy="77" r="3" fill="#60a5fa" opacity=".65" />
                <circle cx="140" cy="123" r="3" fill="#a78bfa" opacity=".65" />
                <circle cx="100" cy="146" r="3.5" fill="#a78bfa" opacity=".75" />
                <circle cx="60" cy="77" r="3" fill="#60a5fa" opacity=".65" />
                <circle cx="60" cy="123" r="3" fill="#a78bfa" opacity=".65" />
                <line x1="100" y1="70" x2="100" y2="54" stroke="rgba(108,71,255,.3)" strokeWidth="1" />
                <line x1="126" y1="84" x2="140" y2="77" stroke="rgba(108,71,255,.3)" strokeWidth="1" />
                <line x1="126" y1="116" x2="140" y2="123" stroke="rgba(108,71,255,.3)" strokeWidth="1" />
                <line x1="100" y1="130" x2="100" y2="146" stroke="rgba(108,71,255,.3)" strokeWidth="1" />
                <line x1="74" y1="84" x2="60" y2="77" stroke="rgba(108,71,255,.3)" strokeWidth="1" />
                <line x1="74" y1="116" x2="60" y2="123" stroke="rgba(108,71,255,.3)" strokeWidth="1" />

                <circle cx="100" cy="100" r="29" fill="rgba(108,71,255,.35)">
                  <animate attributeName="r" values="29;33;29" dur="2.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values=".7;1;.7" dur="2.8s" repeatCount="indefinite" />
                </circle>
                <circle cx="100" cy="100" r="20" fill="rgba(108,71,255,.65)" filter="url(#eg)" />

                <text x="100" y="106" textAnchor="middle" fontSize="13" fontWeight="800"
                  fontFamily="Plus Jakarta Sans, sans-serif" fill="rgba(196,181,253,.9)" letterSpacing="1">IA</text>
              </svg>
              <div className="engine-overlay" style={{ paddingTop: 90 }}>
                <div className="engine-score-val" id="matchScore">—</div>
                <div className="engine-score-lbl">match</div>
              </div>
            </div>

            {/* Right: AO */}
            <div className="match-card right-card">
              <div className="mc-label">Appel d'offres</div>
              <div className="mc-head">
                <div className="mc-av mc-av-esn">ESN</div>
                <div>
                  <div className="mc-name">Mission Lead Dev</div>
                  <div className="mc-sub">Paris · 6 mois · 650€/j</div>
                </div>
              </div>
              <div className="mc-reqs">
                <div className="mc-req" data-req="React"><span className="mc-req-icon">○</span><span>React</span></div>
                <div className="mc-req" data-req="Node.js"><span className="mc-req-icon">○</span><span>Node.js</span></div>
                <div className="mc-req" data-req="AWS"><span className="mc-req-icon">○</span><span>AWS</span></div>
                <div className="mc-req" data-req="Python"><span className="mc-req-icon">○</span><span>Python</span></div>
                <div className="mc-req" data-req="Docker"><span className="mc-req-icon">○</span><span>Docker</span></div>
              </div>
            </div>
          </div>

          <div className="match-result" id="matchResult">
            <div className="match-result-inner">
              ✦ 94% de compatibilité — Profil recommandé pour cette mission
            </div>
          </div>

          <div className="match-pills fade-up">
            <div className="match-pill">⚡ Analyse en temps réel</div>
            <div className="match-pill">🎯 Score de compatibilité IA</div>
            <div className="match-pill">🔔 Alertes automatiques</div>
            <div className="match-pill">📊 Classement des profils</div>
          </div>
        </div>
      </section>

      {/* LIBRARY */}
      <section id="library">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center fade-up">
            <div className="section-eyebrow">Bibliothèque de profils</div>
            <h2 className="section-title">
              Tous vos consultants,<br />
              <span className="grad">au même endroit.</span>
            </h2>
            <p className="section-sub">
              Fini de fouiller dans vos dossiers. Chaque DC généré est automatiquement
              archivé, indexé et prêt à être retrouvé en 2 secondes.
            </p>
          </div>

          <div className="library-layout">
            {/* Left: mockup */}
            <div className="library-mockup-wrap fade-up">
              <div className="library-mockup-glow"></div>
              <div className="lib-shell">
                <div className="lib-topbar">
                  <div className="lib-search">
                    <span className="lib-search-icon">🔍</span>
                    <span className="lib-search-text" id="libSearchText"><span className="lib-search-cursor"></span></span>
                  </div>
                  <div className="lib-count"><strong id="libCountNum">47</strong> profils</div>
                </div>

                <div className="lib-filters">
                  <span className="lib-chip active" id="libChipAll">Tous</span>
                  <span className="lib-chip" id="libChipDispo">Disponibles</span>
                  <span className="lib-chip" id="libChipReact">React</span>
                  <span className="lib-chip" id="libChipData">Data</span>
                  <span className="lib-chip" id="libChipCloud">Cloud</span>
                </div>

                <div className="lib-grid">
                  <div className="lib-card" id="lc0">
                    <div className="lib-card-top">
                      <div className="lib-avatar" style={{ background: 'linear-gradient(135deg,#6c47ff,#4530d0)' }}>TM</div>
                      <div><div className="lib-name">Thomas Mercier</div><div className="lib-role">Lead Dev FS</div></div>
                    </div>
                    <div className="lib-badge dispo"><span className="lib-badge-dot"></span>Disponible</div>
                    <div className="lib-skills">
                      <span className="lib-skill">React</span>
                      <span className="lib-skill">Node.js</span>
                      <span className="lib-skill">AWS</span>
                    </div>
                  </div>

                  <div className="lib-card" id="lc1">
                    <div className="lib-card-top">
                      <div className="lib-avatar" style={{ background: 'linear-gradient(135deg,#0d9488,#0f766e)' }}>SC</div>
                      <div><div className="lib-name">Sophie Chen</div><div className="lib-role">Data Engineer</div></div>
                    </div>
                    <div className="lib-badge dispo"><span className="lib-badge-dot"></span>Disponible</div>
                    <div className="lib-skills">
                      <span className="lib-skill">Python</span>
                      <span className="lib-skill">Spark</span>
                      <span className="lib-skill">DBT</span>
                    </div>
                  </div>

                  <div className="lib-card" id="lc2">
                    <div className="lib-card-top">
                      <div className="lib-avatar" style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)' }}>AL</div>
                      <div><div className="lib-name">Antoine Lambert</div><div className="lib-role">DevOps / Cloud</div></div>
                    </div>
                    <div className="lib-badge indispo"><span className="lib-badge-dot"></span>En mission</div>
                    <div className="lib-skills">
                      <span className="lib-skill">Kubernetes</span>
                      <span className="lib-skill">Terraform</span>
                    </div>
                  </div>

                  <div className="lib-card" id="lc3">
                    <div className="lib-card-top">
                      <div className="lib-avatar" style={{ background: 'linear-gradient(135deg,#dc2626,#991b1b)' }}>MR</div>
                      <div><div className="lib-name">Marie Rousseau</div><div className="lib-role">Product Manager</div></div>
                    </div>
                    <div className="lib-badge bientot"><span className="lib-badge-dot"></span>Bientôt dispo</div>
                    <div className="lib-skills">
                      <span className="lib-skill">Agile</span>
                      <span className="lib-skill">Figma</span>
                    </div>
                  </div>

                  <div className="lib-card" id="lc4">
                    <div className="lib-card-top">
                      <div className="lib-avatar" style={{ background: 'linear-gradient(135deg,#059669,#047857)' }}>JD</div>
                      <div><div className="lib-name">Julien Dupont</div><div className="lib-role">Architecte SI</div></div>
                    </div>
                    <div className="lib-badge dispo"><span className="lib-badge-dot"></span>Disponible</div>
                    <div className="lib-skills">
                      <span className="lib-skill">Java</span>
                      <span className="lib-skill">Spring</span>
                      <span className="lib-skill">Azure</span>
                    </div>
                  </div>

                  <div className="lib-card" id="lc5">
                    <div className="lib-card-top">
                      <div className="lib-avatar" style={{ background: 'linear-gradient(135deg,#d97706,#b45309)' }}>LB</div>
                      <div><div className="lib-name">Laura Benali</div><div className="lib-role">UX Designer</div></div>
                    </div>
                    <div className="lib-badge indispo"><span className="lib-badge-dot"></span>En mission</div>
                    <div className="lib-skills">
                      <span className="lib-skill">Figma</span>
                      <span className="lib-skill">Prototyping</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: feature list */}
            <div>
              <div className="section-eyebrow left">Gestion de profils</div>
              <h2 className="section-title">
                Votre base de consultants,<br />
                <span className="grad">enfin organisée.</span>
              </h2>
              <p className="section-sub" style={{ marginBottom: 0 }}>
                Plus besoin de jongler entre mails, SharePoints et fichiers éparpillés.
                SkillDeck centralise, structure et met à jour chaque profil automatiquement.
              </p>
              <div className="lib-features">
                <div className="lib-feature fade-up">
                  <div className="lib-feature-icon">⚡</div>
                  <div>
                    <h4>Recherche instantanée</h4>
                    <p>Filtrez par compétence, disponibilité, TJM ou localisation. Trouvez le bon profil en 2 secondes sur 500 consultants.</p>
                  </div>
                </div>
                <div className="lib-feature fade-up" style={{ transitionDelay: '.1s' }}>
                  <div className="lib-feature-icon">🔄</div>
                  <div>
                    <h4>Profils toujours à jour</h4>
                    <p>Uploader un nouveau CV met automatiquement à jour le DC existant. Fini les versions obsolètes qui circulent par email.</p>
                  </div>
                </div>
                <div className="lib-feature fade-up" style={{ transitionDelay: '.2s' }}>
                  <div className="lib-feature-icon">📤</div>
                  <div>
                    <h4>Export en 1 clic</h4>
                    <p>Envoyez un dossier de compétences en PDF, PPT ou lien partageable directement depuis la bibliothèque — sans ouvrir le profil.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRM — comptes clients & pilotage commercial */}
      <section id="crm">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center fade-up">
            <div className="section-eyebrow">Nouveau · CRM client</div>
            <h2 className="section-title">
              Vos comptes clients,<br />
              <span className="grad">équipe par équipe.</span>
            </h2>
            <p className="section-sub">
              Un grand compte, ce n'est pas une ligne dans un tableur : c'est des entités,
              des dizaines d'équipes et des interlocuteurs qui changent. SkillDeck en garde
              la carte, et vous dit qui relancer.
            </p>
          </div>

          <div className="crm-split">
            <div className="crm-visual fade-up">
              <div className="crm-tree">
                <div className="crm-node">
                  <span className="crm-ic">BP</span>
                  <div>
                    <div className="crm-nm">BNP Paribas</div>
                    <div className="crm-sub">Banque · Paris</div>
                  </div>
                </div>
                <div className="crm-node lvl1">
                  <span className="crm-ic">◧</span>
                  <div>
                    <div className="crm-nm">BNP CIB</div>
                    <div className="crm-sub">2 équipes cartographiées</div>
                  </div>
                </div>
                <div className="crm-node lvl2">
                  <span className="crm-ic">◇</span>
                  <div>
                    <div className="crm-nm">Cash Management</div>
                    <div className="crm-sub">Inès Laval · Responsable</div>
                  </div>
                  <span className="crm-pill">Active</span>
                </div>
                <div className="crm-node lvl2">
                  <span className="crm-ic">◇</span>
                  <div>
                    <div className="crm-nm">Services Paiements</div>
                    <div className="crm-sub">Aucun contact référencé</div>
                  </div>
                  <span className="crm-pill warn">Cible</span>
                </div>
              </div>
              <div className="crm-alert">
                ⏰ 3 clients sans contact depuis plus de 30 jours
              </div>
            </div>

            <div className="crm-points">
              <div className="crm-point fade-up">
                <div className="crm-point-ic">🗺️</div>
                <div>
                  <h3>Le périmètre, en clair</h3>
                  <p>
                    Compte, entité, équipe : vous cartographiez qui fait quoi chez votre
                    client, et où vous êtes déjà présent. Fini le compte « BNP » qui ne
                    veut rien dire.
                  </p>
                </div>
              </div>
              <div className="crm-point fade-up" style={{ transitionDelay: '.1s' }}>
                <div className="crm-point-ic">👤</div>
                <div>
                  <h3>La fiche qui compte</h3>
                  <p>
                    Fonction, décisionnaire ou non, sujets à aborder, anniversaire,
                    missions déjà placées — et l'historique daté de chaque échange.
                  </p>
                </div>
              </div>
              <div className="crm-point fade-up" style={{ transitionDelay: '.2s' }}>
                <div className="crm-point-ic">⏰</div>
                <div>
                  <h3>Les relances qui vous échappent</h3>
                  <p>
                    Un client sans contact depuis 30 jours remonte tout seul. Vous ne
                    perdez plus un compte parce qu'il est passé sous le radar.
                  </p>
                </div>
              </div>
              <div className="crm-point fade-up" style={{ transitionDelay: '.3s' }}>
                <div className="crm-point-ic">🎯</div>
                <div>
                  <h3>Le bon profil, à la bonne équipe</h3>
                  <p>
                    Un consultant se libère ? Vous voyez immédiatement quelles équipes
                    cibler et qui contacter, sans ressortir vos notes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how">
        <div className="container">
          <div className="text-center fade-up">
            <div className="section-eyebrow">Comment ça marche</div>
            <h2 className="section-title">
              Trois étapes.<br />
              <span className="grad">Un placement.</span>
            </h2>
          </div>
          <div className="steps-container">
            <div className="step fade-up">
              <div className="step-badge"><span className="step-badge-num">1</span></div>
              <h3>Centralisez votre vivier</h3>
              <p>Importez vos CV : SkillDeck en fait des profils structurés, indexés et cherchables en quelques secondes.</p>
            </div>
            <div className="step fade-up">
              <div className="step-badge"><span className="step-badge-num">2</span></div>
              <h3>Matchez sur les besoins</h3>
              <p>Chaque appel d'offres est comparé à votre vivier. L'IA remonte les meilleurs profils avec un score de compatibilité.</p>
            </div>
            <div className="step fade-up">
              <div className="step-badge"><span className="step-badge-num">3</span></div>
              <h3>Positionnez & relancez</h3>
              <p>Générez le dossier de compétences, envoyez-le au client et automatisez la relance — sans quitter SkillDeck.</p>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="usecases">
        <div className="container">
          <div className="text-center fade-up">
            <div className="section-eyebrow">Cas d'usage</div>
            <h2 className="section-title">
              Fait pour ceux qui<br />
              <span className="grad">vendent des compétences.</span>
            </h2>
          </div>
          <div className="cases-grid">
            <div className="case-card fade-up">
              <div className="case-emoji-wrap">🏢</div>
              <div className="case-target">ESN & Sociétés de conseil</div>
              <h3>Placez vos consultants avant la concurrence</h3>
              <p>Du besoin capté au dossier envoyé : matchez votre vivier aux appels d'offres et positionnez le bon profil en quelques minutes. Ne perdez plus jamais un marché par manque de réactivité.</p>
              <div className="case-tags">
                <span className="tag">Business managers</span>
                <span className="tag">Appels d'offres</span>
                <span className="tag">Commercial</span>
              </div>
            </div>
            <div className="case-card fade-up">
              <div className="case-emoji-wrap">🔍</div>
              <div className="case-target">Cabinets de recrutement</div>
              <h3>Présentez vos candidats avec impact</h3>
              <p>Transformez chaque CV reçu en une présentation professionnelle qui valorise le talent et impressionne vos clients.</p>
              <div className="case-tags">
                <span className="tag">Chargés de recrutement</span>
                <span className="tag">Présentations client</span>
              </div>
            </div>
            <div className="case-card fade-up">
              <div className="case-emoji-wrap">💻</div>
              <div className="case-target">Freelances IT</div>
              <h3>Valorisez votre profil en un instant</h3>
              <p>Créez un dossier de compétences percutant pour chaque mission. Démarquez-vous et décrochez plus de contrats.</p>
              <div className="case-tags">
                <span className="tag">Dev & Tech</span>
                <span className="tag">Consultants</span>
                <span className="tag">Experts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center fade-up">
            <div className="section-eyebrow">Tarifs</div>
            <h2 className="section-title">
              Simple, transparent,<br />
              <span className="grad">sans surprise.</span>
            </h2>
            <p className="section-sub">Commencez gratuitement. Montez en puissance quand vous en avez besoin.</p>
          </div>
          <div className="pricing-grid">
            {/* STARTER */}
            <div className="pricing-card fade-up">
              <div className="pricing-plan">Starter</div>
              <div className="pricing-price">Gratuit<span> / mois</span></div>
              <div className="pricing-desc">Pour découvrir SkillDeck. 1 utilisateur inclus.</div>
              <div className="pricing-sep"></div>
              <ul className="pricing-features">
                <li><span className="check"></span>1 utilisateur</li>
                <li><span className="check"></span>4 dossiers / mois</li>
                <li><span className="check"></span>Upload PDF & Word</li>
                <li><span className="check"></span>Export PDF</li>
                <li><span className="check"></span>1 template inclus</li>
              </ul>
              <button className="btn btn-ghost" onClick={goRegister}>Commencer gratuitement</button>
            </div>

            {/* PRO */}
            <div className="pricing-card popular fade-up">
              <div className="popular-badge">⚡ Le plus populaire</div>
              <div className="pricing-plan">Pro</div>
              <div className="pricing-price">49€<span> / mois</span></div>
              <div className="pricing-desc">Pour les indépendants et consultants solo.</div>
              <div className="pricing-sep"></div>
              <ul className="pricing-features">
                <li><span className="check"></span>1 utilisateur</li>
                <li><span className="check"></span>50 dossiers / mois</li>
                <li><span className="check"></span>Export PDF, PPT & lien partageable</li>
                <li><span className="check"></span>Bibliothèque 20 templates</li>
                <li><span className="check"></span>Logo & couleurs personnalisés</li>
                <li><span className="check"></span>Support prioritaire</li>
              </ul>
              <button className="btn btn-primary" onClick={goRegister}>Démarrer en Pro</button>
            </div>

            {/* TEAM */}
            <div className="pricing-card fade-up">
              <div className="pricing-plan">Team</div>
              <div className="pricing-price">199€<span> / mois</span></div>
              <div className="pricing-desc">Pour les équipes et ESN. Jusqu'à 5 utilisateurs.</div>
              <div className="pricing-sep"></div>
              <ul className="pricing-features">
                <li><span className="check"></span>5 utilisateurs inclus</li>
                <li><span className="check"></span>Dossiers illimités</li>
                <li><span className="check"></span>Tous les exports inclus</li>
                <li><span className="check"></span>Templates partagés</li>
                <li><span className="check"></span>Tableau de bord équipe</li>
                <li><span className="check"></span>Support prioritaire</li>
              </ul>
              <button className="btn btn-ghost" onClick={goRegister}>Démarrer en Team</button>
            </div>

            {/* ENTERPRISE */}
            <div className="pricing-card fade-up">
              <div className="pricing-plan">Enterprise</div>
              <div className="pricing-price" style={{ fontSize: '1.9rem', letterSpacing: '-.5px' }}>Sur demande</div>
              <div className="pricing-desc">Pour les grandes structures avec des besoins sur-mesure.</div>
              <div className="pricing-sep"></div>
              <ul className="pricing-features">
                <li><span className="check"></span>Utilisateurs illimités</li>
                <li><span className="check"></span>Dossiers illimités</li>
                <li><span className="check"></span>Intégration ATS / CRM</li>
                <li><span className="check"></span>Templates à votre charte</li>
                <li><span className="check"></span>Account manager dédié</li>
                <li><span className="check"></span>SLA garanti</li>
              </ul>
              <a href="mailto:contact@skilldeck.com" className="btn btn-ghost">Contacter les ventes</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section id="cta">
        <div className="container">
          <div className="cta-inner fade-up">
            <div className="cta-badge">✦ Prêt à commencer ?</div>
            <h2>
              Placez votre prochain consultant<br />
              <span className="grad">plus vite. Essayez gratuitement.</span>
            </h2>
            <p>Rejoignez les business managers qui accélèrent leurs placements avec SkillDeck.</p>
            <div className="cta-buttons">
              <button className="btn btn-primary btn-lg" onClick={goRegister}>Essayer gratuitement →</button>
              <button className="btn btn-ghost btn-lg" onClick={goRegister}>Demander une démo</button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="footer-inner">
            <div className="footer-brand">
              <a href="#" className="nav-logo" style={{ marginBottom: 0 }} onClick={(e) => e.preventDefault()}>
                <SkillDeckLogo size={28} />
                <span className="nav-logo-text">Skill<span>Deck</span></span>
              </a>
              <p>Le copilot sales des business managers en ESN. Du besoin client au consultant placé.</p>
            </div>
            <div className="footer-col">
              <h4>Produit</h4>
              <ul>
                <li><a href="#features">Fonctionnalités</a></li>
                <li><a href="#how">Comment ça marche</a></li>
                <li><a href="#pricing">Tarifs</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Cas d'usage</h4>
              <ul>
                <li><a href="#usecases">ESN & Conseil</a></li>
                <li><a href="#usecases">Recrutement</a></li>
                <li><a href="#usecases">Freelances IT</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Entreprise</h4>
              <ul>
                <li><a href="#">À propos</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="mailto:contact@skilldeck.com">Contact</a></li>
                <li><a href="#">Mentions légales</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} SkillDeck. Tous droits réservés.</p>
            <p>Fait avec ⚡ pour les business managers en ESN</p>
          </div>
        </div>
      </footer>

      {/* LOGIN SLIDE-IN PANEL */}
      <div className={`sd-login-overlay ${showLogin ? 'sd-login-overlay-visible' : ''}`} onClick={() => setShowLogin(false)} />
      <div className={`sd-login-panel ${showLogin ? 'sd-login-panel-open' : ''}`}>
        <button className="sd-login-close" onClick={() => setShowLogin(false)} aria-label="Fermer">✕</button>
        <div className="sd-login-panel-inner">
          <div className="sd-login-panel-header">
            <SkillDeckLogo size={48} />
            <h2>Connexion</h2>
            <p>Accédez à votre espace de gestion de dossiers</p>
          </div>
          <form onSubmit={handleLoginSubmit} className="sd-login-form">
            <div className="sd-login-field">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                placeholder="votre@email.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className={loginErrors.email ? 'sd-input-error' : ''}
              />
              {loginErrors.email && <span className="sd-field-error">{loginErrors.email}</span>}
            </div>
            <div className="sd-login-field">
              <label htmlFor="login-password">Mot de passe</label>
              <input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className={loginErrors.password ? 'sd-input-error' : ''}
              />
              {loginErrors.password && <span className="sd-field-error">{loginErrors.password}</span>}
            </div>
            <div style={{ textAlign: 'right', marginTop: '-4px', marginBottom: '4px' }}>
              <button
                type="button"
                className="sd-link-btn"
                style={{ fontSize: '12px' }}
                onClick={() => { setShowLogin(false); navigate('/forgot-password'); }}
              >
                Mot de passe oublié ?
              </button>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={loginLoading}
            >
              {loginLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={16} className="sd-spin" /> Connexion...
                </span>
              ) : 'Se connecter'}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0 2px' }}>
              <span style={{ height: 1, flex: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 12, color: 'var(--muted2)' }}>ou</span>
              <span style={{ height: 1, flex: 1, background: 'var(--border)' }} />
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loginLoading || googleLoading}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '11px 16px', borderRadius: 10, cursor: 'pointer',
                background: '#fff', color: '#1f2937', fontWeight: 600, fontSize: 14,
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              {googleLoading ? (
                <Loader2 size={16} className="sd-spin" />
              ) : (
                <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33z"/>
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
                </svg>
              )}
              Continuer avec Google
            </button>
          </form>
          <p className="sd-login-footer-text">
            Pas encore de compte ?{' '}
            <button className="sd-link-btn" onClick={() => { setShowLogin(false); navigate('/register'); }}>
              S'inscrire
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

const landingCSS = `
  /* ── Scoped reset & base ── */
  .sd-v2, .sd-v2 *, .sd-v2 *::before, .sd-v2 *::after { box-sizing: border-box; }
  .sd-v2 *, .sd-v2 *::before, .sd-v2 *::after { margin: 0; padding: 0; }
  .sd-v2 {
    --bg:        #05050f;
    --bg2:       #080818;
    --violet:    #6c47ff;
    --blue:      #2563eb;
    --violet2:   #a78bfa;
    --violet3:   #c4b5fd;
    --text:      #f0eeff;
    --muted:     #8b7cf6;
    --muted2:    #5a5878;
    --border:    rgba(108,71,255,.18);
    --border2:   rgba(255,255,255,.06);
    --card-bg:   rgba(255,255,255,.03);
    --glass:     rgba(108,71,255,.07);
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: var(--bg);
    color: var(--text);
    line-height: 1.6;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
    scroll-behavior: smooth;
  }

  /* Typography */
  .sd-v2 h1 { font-size: clamp(2.6rem, 6vw, 4.4rem); font-weight: 800; letter-spacing: -2.5px; line-height: 1.08; }
  .sd-v2 h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 700; letter-spacing: -1.5px; }
  .sd-v2 h3 { font-size: 1.1rem; font-weight: 600; letter-spacing: -.3px; }
  .sd-v2 p  { color: #7a75a0; line-height: 1.7; }

  .sd-v2 .grad {
    background: linear-gradient(135deg, #fff 0%, var(--violet3) 55%, #60a5fa 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .sd-v2 .container { max-width: 1160px; margin: 0 auto; padding: 0 28px; }
  .sd-v2 section { padding: 120px 0; position: relative; }

  /* Noise overlay */
  .sd-v2::before {
    content: '';
    position: fixed; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");
    pointer-events: none; z-index: 0; opacity: .5;
  }

  /* NAV */
  .sd-v2 nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    padding: 0 28px;
    height: 64px;
    display: flex; align-items: center; justify-content: space-between;
    background: rgba(5,5,15,.75);
    backdrop-filter: blur(24px) saturate(1.4);
    border-bottom: 1px solid var(--border);
  }
  .sd-v2 .nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
  .sd-v2 .nav-logo-text {
    font-size: 1.15rem; font-weight: 700; letter-spacing: -.5px;
    color: #fff;
  }
  .sd-v2 .nav-logo-text span { color: var(--violet3); }
  .sd-v2 .nav-links { display: flex; gap: 6px; list-style: none; }
  .sd-v2 .nav-links a {
    color: #7a75a0; text-decoration: none; font-size: .875rem; font-weight: 500;
    padding: 6px 14px; border-radius: 8px; transition: all .2s;
  }
  .sd-v2 .nav-links a:hover { color: #fff; background: rgba(255,255,255,.05); }
  .sd-v2 .btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 9px 20px; border-radius: 10px;
    font-size: .875rem; font-weight: 600;
    cursor: pointer; transition: all .22s;
    text-decoration: none; border: none;
    font-family: inherit;
  }
  .sd-v2 .btn-primary {
    background: linear-gradient(135deg, var(--violet) 0%, #4f35d8 100%);
    color: #fff;
    box-shadow: 0 0 0 1px rgba(108,71,255,.4), 0 4px 24px rgba(108,71,255,.35);
  }
  .sd-v2 .btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px rgba(108,71,255,.6), 0 8px 32px rgba(108,71,255,.5);
  }
  .sd-v2 .btn-ghost {
    background: rgba(255,255,255,.05);
    color: #c4bde0;
    border: 1px solid var(--border2);
  }
  .sd-v2 .btn-ghost:hover { background: rgba(255,255,255,.09); color: #fff; }
  .sd-v2 .btn-lg { padding: 13px 28px; font-size: .95rem; border-radius: 12px; }

  /* Boutons de la barre — déclarés AVANT le bloc responsive, sinon cette règle
     l'emporterait sur le display:none du mobile (spécificité égale). */
  .sd-v2 nav .nav-actions { display: flex; gap: 8px; align-items: center; }
  .sd-v2 nav .nav-actions .btn { padding: 8px 16px; white-space: nowrap; }

  /* Mobile menu button */
  .sd-v2 .mobile-menu-btn {
    display: none; background: none; border: none; color: #fff; cursor: pointer;
    padding: 8px; border-radius: 8px; transition: background .2s;
  }
  .sd-v2 .mobile-menu-btn:hover { background: rgba(108,71,255,.15); }
  .sd-v2 .mobile-menu {
    position: fixed; top: 64px; left: 0; right: 0; bottom: 0; z-index: 99;
    background: rgba(5,5,15,.97); backdrop-filter: blur(20px);
    padding: 24px; display: flex; flex-direction: column; gap: 32px;
    animation: sd-v2-menu-slide .25s ease;
  }
  @keyframes sd-v2-menu-slide { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
  .sd-v2 .mobile-menu-links { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .sd-v2 .mobile-menu-links li a {
    display: block; padding: 14px 16px; color: #c4bde0; font-size: 1rem; font-weight: 500;
    text-decoration: none; border-radius: 10px; transition: all .2s;
  }
  .sd-v2 .mobile-menu-links li a:hover { color: #fff; background: rgba(108,71,255,.1); }
  .sd-v2 .mobile-menu-actions { display: flex; flex-direction: column; gap: 10px; }

  /* HERO */
  .sd-v2 #hero {
    position: relative; min-height: 100vh;
    display: flex; align-items: center;
    padding-top: 80px;
    overflow: hidden;
  }
  .sd-v2 .aurora-bg { position: absolute; inset: 0; z-index: 0; overflow: hidden; }
  .sd-v2 .aurora-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0;
    animation: sd-v2-aurora-appear .8s ease forwards;
  }
  .sd-v2 .ab1 {
    width: 800px; height: 800px;
    background: radial-gradient(ellipse, rgba(108,71,255,.28) 0%, transparent 70%);
    top: -300px; left: -200px;
    animation: sd-v2-ab1-move 14s ease-in-out infinite alternate, sd-v2-aurora-appear .8s ease forwards;
  }
  .sd-v2 .ab2 {
    width: 600px; height: 600px;
    background: radial-gradient(ellipse, rgba(37,99,235,.22) 0%, transparent 70%);
    bottom: -200px; right: -100px;
    animation: sd-v2-ab2-move 18s ease-in-out infinite alternate, sd-v2-aurora-appear 1s .2s ease forwards;
  }
  .sd-v2 .ab3 {
    width: 500px; height: 500px;
    background: radial-gradient(ellipse, rgba(167,139,250,.15) 0%, transparent 70%);
    top: 40%; left: 50%;
    transform: translate(-50%, -50%);
    animation: sd-v2-ab3-move 11s ease-in-out infinite alternate, sd-v2-aurora-appear 1.2s .4s ease forwards;
  }
  @keyframes sd-v2-aurora-appear { to { opacity: 1; } }
  @keyframes sd-v2-ab1-move { 0%{transform:translate(0,0) scale(1)} 100%{transform:translate(120px,80px) scale(1.15)} }
  @keyframes sd-v2-ab2-move { 0%{transform:translate(0,0) scale(1)} 100%{transform:translate(-80px,-60px) scale(1.1)} }
  @keyframes sd-v2-ab3-move { 0%{transform:translate(-50%,-50%) scale(1)} 100%{transform:translate(-50%,-50%) scale(1.2)} }

  .sd-v2 .hero-grid {
    position: absolute; inset: 0; z-index: 0;
    background-image:
      linear-gradient(rgba(108,71,255,.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(108,71,255,.04) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, black 20%, transparent 80%);
    -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, black 20%, transparent 80%);
  }

  .sd-v2 #hero::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 200px;
    background: linear-gradient(to bottom, transparent, var(--bg));
    z-index: 1; pointer-events: none;
  }

  .sd-v2 .hero-inner {
    position: relative; z-index: 2;
    display: flex; align-items: center; gap: 64px;
  }
  .sd-v2 .hero-content { flex: 1; min-width: 0; max-width: 540px; }

  .sd-v2 .hero-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(108,71,255,.1);
    border: 1px solid rgba(108,71,255,.25);
    border-radius: 100px; padding: 5px 14px; font-size: .78rem;
    font-weight: 600; color: var(--violet2); margin-bottom: 32px;
    letter-spacing: .3px;
  }
  .sd-v2 .hero-eyebrow::before {
    content: ''; width: 6px; height: 6px; background: var(--violet2);
    border-radius: 50%; animation: sd-v2-blink 2s ease-in-out infinite;
  }
  @keyframes sd-v2-blink { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.8)} }

  .sd-v2 .hero-eyebrow-link { text-decoration: none; transition: all .16s; cursor: pointer; }
  .sd-v2 .hero-eyebrow-link:hover { background: rgba(108,71,255,.18); border-color: rgba(108,71,255,.5); color: var(--violet3); }

  /* ── Avant / après ── */
  .sd-v2 .ba { max-width: 940px; margin: 0 auto; }
  .sd-v2 .ba-legend {
    display: grid; grid-template-columns: 250px 1fr 26px 1fr; gap: 14px;
    margin-bottom: 10px;
  }
  .sd-v2 .ba-legend span {
    font-size: .7rem; font-weight: 700; letter-spacing: .8px; text-transform: uppercase;
    padding-left: 18px;
  }
  .sd-v2 .ba-legend-old { color: #55536e; }
  .sd-v2 .ba-legend-new { color: var(--violet2); }

  .sd-v2 .ba-row {
    display: grid;
    grid-template-columns: 250px 1fr 26px 1fr;
    align-items: stretch; gap: 14px;
    padding: 14px 0; border-top: 1px solid var(--border2);
  }
  .sd-v2 .ba-task {
    display: flex; align-items: center;
    font-size: .95rem; font-weight: 700; color: var(--text); letter-spacing: -.2px;
  }
  .sd-v2 .ba-cell {
    display: flex; flex-direction: column; gap: 4px; justify-content: center;
    padding: 14px 18px; border-radius: 12px;
  }
  .sd-v2 .ba-old {
    background: rgba(255,255,255,.02); border: 1px solid var(--border2);
  }
  .sd-v2 .ba-new {
    background: rgba(108,71,255,.08); border: 1px solid rgba(108,71,255,.28);
  }
  .sd-v2 .ba-lead { font-size: 1rem; font-weight: 800; letter-spacing: -.3px; }
  .sd-v2 .ba-old .ba-lead { color: #6a6885; }
  .sd-v2 .ba-new .ba-lead { color: var(--violet3); }
  .sd-v2 .ba-detail { font-size: .84rem; line-height: 1.5; }
  .sd-v2 .ba-old .ba-detail { color: #4e4c66; }
  .sd-v2 .ba-new .ba-detail { color: #9c96c4; }
  .sd-v2 .ba-arrow {
    display: flex; align-items: center; justify-content: center;
    color: var(--violet2); opacity: .5; font-size: 1rem;
  }

  @media (max-width: 860px) {
    .sd-v2 .ba-legend { display: none; }
    .sd-v2 .ba-row { grid-template-columns: 1fr; gap: 8px; padding: 18px 0; }
    .sd-v2 .ba-arrow { transform: rotate(90deg); height: 14px; }
    .sd-v2 .ba-task { margin-bottom: 2px; }
    .sd-v2 .ba-old::before, .sd-v2 .ba-new::before {
      content: 'Ancienne méthode';
      font-size: .62rem; font-weight: 700; letter-spacing: .7px; text-transform: uppercase;
      color: #55536e; margin-bottom: 2px;
    }
    .sd-v2 .ba-new::before { content: 'Nouvelle méthode'; color: var(--violet2); }
  }

  /* ── Agent section ── */
  .sd-v2 #agent { position: relative; background: linear-gradient(180deg, var(--bg), var(--bg2) 50%, var(--bg)); }
  .sd-v2 .agent-pills {
    display: flex; justify-content: center; flex-wrap: wrap; gap: 10px; margin-top: 30px;
  }
  .sd-v2 .agent-pill {
    background: var(--card-bg); border: 1px solid var(--border2); border-radius: 100px;
    padding: 8px 16px; font-size: .8rem; color: #7a75a0; font-weight: 500;
  }

  .sd-v2 .hero-title { margin-bottom: 22px; }
  .sd-v2 .hero-sub {
    font-size: 1.1rem; color: #7a75a0; max-width: 480px;
    margin-bottom: 44px; line-height: 1.75;
  }
  .sd-v2 .hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 52px; }

  .sd-v2 .hero-trust {
    display: flex; align-items: center; gap: 14px;
    color: var(--muted2); font-size: .82rem;
    border-top: 1px solid var(--border2);
    padding-top: 20px;
  }
  .sd-v2 .hero-trust-avatars { display: flex; }
  .sd-v2 .hero-trust-avatars .av {
    width: 30px; height: 30px; border-radius: 50%;
    border: 2px solid rgba(5,5,15,.8);
    background: linear-gradient(135deg, var(--violet), var(--blue));
    margin-left: -7px; display: flex; align-items: center; justify-content: center;
    font-size: .58rem; font-weight: 700; color: #fff;
  }
  .sd-v2 .hero-trust-avatars .av:first-child { margin-left: 0; }
  .sd-v2 .hero-trust-text { color: #5a5878; }
  .sd-v2 .hero-trust-text strong { color: #a09cb8; font-weight: 600; }

  /* Hero mockup */
  .sd-v2 .hero-mockup { position: relative; flex-shrink: 0; width: 420px; z-index: 2; }
  .sd-v2 .mockup-shell {
    background: rgba(8,8,24,.95);
    border: 1px solid rgba(255,255,255,.09);
    border-radius: 20px;
    overflow: hidden;
    box-shadow:
      0 0 0 1px rgba(108,71,255,.15),
      0 32px 80px rgba(0,0,0,.7),
      0 0 80px rgba(108,71,255,.12),
      inset 0 1px 0 rgba(255,255,255,.08);
  }
  .sd-v2 .mockup-titlebar {
    background: rgba(255,255,255,.03);
    padding: 12px 16px;
    display: flex; align-items: center; gap: 12px;
    border-bottom: 1px solid rgba(255,255,255,.05);
  }
  .sd-v2 .traffic-lights { display: flex; gap: 6px; }
  .sd-v2 .tl { width: 9px; height: 9px; border-radius: 50%; }
  .sd-v2 .tl-r { background: #ff5f57; } .sd-v2 .tl-y { background: #febc2e; } .sd-v2 .tl-g { background: #28c840; }
  .sd-v2 .mockup-url {
    flex: 1; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.06);
    border-radius: 6px; padding: 4px 10px; font-size: .68rem; color: #4a4870;
    text-align: center;
  }
  .sd-v2 .mockup-body { padding: 10px; }

  .sd-v2 .hero-glow {
    position: absolute; width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(108,71,255,.25), transparent 70%);
    border-radius: 50%; filter: blur(40px);
    top: -60px; right: -60px; pointer-events: none; z-index: 1;
  }

  /* App UI mockup */
  .sd-v2 .app-ui {
    border-radius: 8px;
    background: rgba(8,8,24,.98);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 9px;
    min-height: 360px;
  }
  .sd-v2 .aui-upload {
    border: 1.5px dashed rgba(108,71,255,.32);
    border-radius: 10px;
    padding: 11px 13px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(108,71,255,.05);
    opacity: 0; transform: translateY(5px);
    transition: opacity .45s ease, transform .45s ease;
  }
  .sd-v2 .aui-upload.show { opacity: 1; transform: translateY(0); }
  .sd-v2 .aui-upload-icon { font-size: 1.35rem; flex-shrink: 0; }
  .sd-v2 .aui-filename { font-size: .75rem; font-weight: 700; color: #e0dbff; }
  .sd-v2 .aui-upload-ok { font-size: .71rem; color: #4a4870; }

  .sd-v2 .aui-status {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px;
    background: rgba(255,255,255,.03);
    border: 1px solid rgba(255,255,255,.06);
    border-radius: 8px;
    opacity: 0;
    transition: opacity .4s ease;
  }
  .sd-v2 .aui-status.show { opacity: 1; }
  .sd-v2 .aui-status-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #f59e0b; flex-shrink: 0;
    animation: sd-v2-aui-pulse 1.4s ease-in-out infinite;
  }
  .sd-v2 .aui-status-dot.done { background: #10b981; animation: none; }
  @keyframes sd-v2-aui-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: .45; transform: scale(.75); }
  }
  .sd-v2 .aui-status-text { font-size: .73rem; font-weight: 600; color: #c4b5fd; flex: 1; }
  .sd-v2 .aui-status-time { font-size: .68rem; color: #4a4870; }

  .sd-v2 .aui-profile {
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.07);
    border-radius: 10px;
    padding: 11px 13px;
    display: flex; flex-direction: column; gap: 9px;
    opacity: 0; transform: translateY(8px);
    transition: opacity .5s ease, transform .5s ease;
  }
  .sd-v2 .aui-profile.show { opacity: 1; transform: translateY(0); }
  .sd-v2 .aui-profile-top { display: flex; align-items: center; gap: 10px; }
  .sd-v2 .aui-avatar {
    width: 34px; height: 34px; border-radius: 9px;
    background: linear-gradient(135deg, #6c47ff, #4530d0);
    display: flex; align-items: center; justify-content: center;
    font-size: .72rem; font-weight: 800; color: #fff; flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(108,71,255,.45);
  }
  .sd-v2 .aui-name { font-size: .84rem; font-weight: 700; color: #f0eeff; }
  .sd-v2 .aui-role-text { font-size: .67rem; color: #5a5878; margin-top: 2px; }
  .sd-v2 .aui-tags { display: flex; flex-wrap: wrap; gap: 5px; }
  .sd-v2 .aui-tag {
    padding: 3px 9px; border-radius: 5px;
    font-size: .63rem; font-weight: 700;
    background: rgba(108,71,255,.13);
    color: #a78bfa;
    border: 1px solid rgba(108,71,255,.22);
    opacity: 0; transform: scale(.82);
    transition: opacity .3s ease, transform .3s ease;
  }
  .sd-v2 .aui-tag.show { opacity: 1; transform: scale(1); }

  .sd-v2 .aui-dc {
    background: rgba(255,255,255,.03);
    border: 1px solid rgba(255,255,255,.07);
    border-radius: 10px;
    padding: 11px 13px;
    display: flex; flex-direction: column; gap: 9px;
    opacity: 0; transform: translateY(8px);
    transition: opacity .5s ease, transform .5s ease;
  }
  .sd-v2 .aui-dc.show { opacity: 1; transform: translateY(0); }
  .sd-v2 .aui-dc-header {
    font-size: .62rem; font-weight: 800;
    letter-spacing: 1.5px; text-transform: uppercase;
    color: #5a5878;
  }
  .sd-v2 .aui-bars { display: flex; flex-direction: column; gap: 6px; }
  .sd-v2 .aui-bar-track {
    height: 5px; background: rgba(255,255,255,.06);
    border-radius: 3px; overflow: hidden;
  }
  .sd-v2 .aui-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #6c47ff, #2563eb);
    border-radius: 3px; width: 0%;
    transition: width 1.1s cubic-bezier(.4,0,.2,1);
  }
  .sd-v2 .aui-actions { display: flex; align-items: center; justify-content: space-between; }
  .sd-v2 .aui-exports { display: flex; gap: 5px; }
  .sd-v2 .aui-btn-export {
    padding: 4px 11px; border-radius: 6px;
    font-size: .68rem; font-weight: 700;
    background: rgba(255,255,255,.06);
    color: #9d96c0;
    border: 1px solid rgba(255,255,255,.08);
    cursor: pointer; font-family: inherit;
    opacity: 0; transform: translateY(4px);
    transition: opacity .3s ease, transform .3s ease;
  }
  .sd-v2 .aui-btn-export.show { opacity: 1; transform: translateY(0); }
  .sd-v2 .aui-btn-link { color: var(--violet2) !important; }
  .sd-v2 .aui-gen-time {
    font-size: .65rem; color: #4a4870;
    opacity: 0; transition: opacity .5s ease;
  }
  .sd-v2 .aui-gen-time.show { opacity: 1; }

  /* STATS */
  .sd-v2 #stats {
    padding: 0;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    background: rgba(255,255,255,.015);
    position: relative; z-index: 2;
  }
  .sd-v2 .stats-inner {
    display: flex; justify-content: space-around;
    padding: 48px 0; gap: 24px; flex-wrap: wrap;
  }
  .sd-v2 .stat { text-align: center; position: relative; }
  .sd-v2 .stat:not(:last-child)::after {
    content: '';
    position: absolute; right: -12px; top: 50%; transform: translateY(-50%);
    width: 1px; height: 40px; background: var(--border);
  }
  .sd-v2 .stat-num {
    font-size: 2.8rem; font-weight: 800; letter-spacing: -2px;
    background: linear-gradient(135deg, #fff 0%, var(--violet3) 60%, #60a5fa 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .sd-v2 .stat-label { font-size: .82rem; color: var(--muted2); margin-top: 4px; font-weight: 500; }

  /* Section labels */
  .sd-v2 .section-eyebrow {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: .73rem; font-weight: 700; letter-spacing: 2px;
    text-transform: uppercase; color: var(--violet2); margin-bottom: 18px;
  }
  .sd-v2 .section-eyebrow::before, .sd-v2 .section-eyebrow::after {
    content: ''; flex: 1; height: 1px; background: var(--border);
    display: block;
  }
  .sd-v2 .section-eyebrow.left::before { display: none; }
  .sd-v2 .section-eyebrow.left::after { max-width: 40px; }
  .sd-v2 .section-title { margin-bottom: 18px; }
  .sd-v2 .section-sub { font-size: 1.05rem; color: #7a75a0; max-width: 500px; line-height: 1.75; }
  .sd-v2 .text-center .section-sub { margin: 0 auto; }
  .sd-v2 .text-center .section-eyebrow { justify-content: center; }
  .sd-v2 .text-center .section-eyebrow::before, .sd-v2 .text-center .section-eyebrow::after { max-width: 60px; }
  .sd-v2 .text-center { text-align: center; }

  /* PROBLEM */
  .sd-v2 #problem { position: relative; }
  .sd-v2 .problem-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
    gap: 16px; margin-top: 56px;
  }
  .sd-v2 .problem-card {
    background: var(--card-bg);
    border: 1px solid var(--border2);
    border-radius: 18px; padding: 30px;
    position: relative; overflow: hidden;
    transition: border-color .3s, transform .3s;
  }
  .sd-v2 .problem-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(108,71,255,.05), transparent 60%);
    opacity: 0; transition: opacity .3s;
  }
  .sd-v2 .problem-card:hover { border-color: rgba(108,71,255,.35); transform: translateY(-4px); }
  .sd-v2 .problem-card:hover::before { opacity: 1; }
  .sd-v2 .problem-icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(255,255,255,.04); border: 1px solid var(--border2);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.3rem; margin-bottom: 18px;
  }
  .sd-v2 .problem-card h3 { margin-bottom: 8px; font-size: .95rem; }
  .sd-v2 .problem-card p { font-size: .88rem; }

  /* FEATURES */
  .sd-v2 #features {
    background: linear-gradient(180deg, transparent 0%, rgba(108,71,255,.04) 50%, transparent 100%);
  }
  .sd-v2 .features-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px; margin-top: 56px;
  }
  .sd-v2 .feature-card {
    background: var(--card-bg);
    border: 1px solid var(--border2);
    border-radius: 18px; padding: 30px;
    position: relative; overflow: hidden; transition: all .3s;
  }
  .sd-v2 .feature-card::after {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(108,71,255,.4), transparent);
    opacity: 0; transition: opacity .3s;
  }
  .sd-v2 .feature-card:hover { border-color: rgba(108,71,255,.3); transform: translateY(-5px); }
  .sd-v2 .feature-card:hover::after { opacity: 1; }
  .sd-v2 .feature-icon {
    width: 46px; height: 46px; border-radius: 13px;
    background: linear-gradient(135deg, rgba(108,71,255,.2), rgba(37,99,235,.12));
    border: 1px solid rgba(108,71,255,.2);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.2rem; margin-bottom: 20px;
  }
  .sd-v2 .feature-card h3 { margin-bottom: 8px; font-size: .95rem; }
  .sd-v2 .feature-card p { font-size: .88rem; }

  /* HOW */
  .sd-v2 #how { position: relative; }
  .sd-v2 .steps-container {
    margin-top: 72px; position: relative;
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 0;
  }
  .sd-v2 .steps-container::before {
    content: '';
    position: absolute;
    top: 32px; left: calc(16.66% + 10px); right: calc(16.66% + 10px);
    height: 1px;
    background: linear-gradient(90deg,
      rgba(108,71,255,.6) 0%,
      rgba(37,99,235,.4) 50%,
      rgba(108,71,255,.6) 100%
    );
    z-index: 0;
  }
  .sd-v2 .step { text-align: center; padding: 0 28px; position: relative; z-index: 1; }
  .sd-v2 .step-badge {
    width: 64px; height: 64px; border-radius: 50%;
    margin: 0 auto 24px;
    background: linear-gradient(135deg, rgba(108,71,255,.2) 0%, rgba(37,99,235,.12) 100%);
    border: 1px solid rgba(108,71,255,.35);
    display: flex; align-items: center; justify-content: center;
    position: relative;
    box-shadow: 0 0 0 8px rgba(108,71,255,.06), 0 0 24px rgba(108,71,255,.15);
  }
  .sd-v2 .step-badge-num { font-size: 1.3rem; font-weight: 800; color: var(--violet2); }
  .sd-v2 .step h3 { font-size: 1rem; margin-bottom: 10px; }
  .sd-v2 .step p { font-size: .88rem; }

  /* USE CASES */
  .sd-v2 #usecases { background: rgba(255,255,255,.01); }
  .sd-v2 .cases-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
    gap: 16px; margin-top: 56px;
  }
  .sd-v2 .case-card {
    background: var(--card-bg);
    border: 1px solid var(--border2);
    border-radius: 22px; padding: 36px 30px;
    position: relative; overflow: hidden; transition: all .3s;
  }
  .sd-v2 .case-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(108,71,255,.06), transparent 70%);
    opacity: 0; transition: opacity .3s;
  }
  .sd-v2 .case-card:hover { border-color: rgba(108,71,255,.3); transform: translateY(-5px); }
  .sd-v2 .case-card:hover::before { opacity: 1; }
  .sd-v2 .case-emoji-wrap {
    width: 52px; height: 52px; border-radius: 16px;
    background: rgba(255,255,255,.04); border: 1px solid var(--border2);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.5rem; margin-bottom: 18px;
  }
  .sd-v2 .case-target {
    font-size: .73rem; color: var(--violet2); font-weight: 700;
    letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 12px;
  }
  .sd-v2 .case-card h3 { font-size: 1.05rem; margin-bottom: 10px; color: #fff; }
  .sd-v2 .case-card p { font-size: .88rem; margin-bottom: 20px; }
  .sd-v2 .case-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .sd-v2 .tag {
    background: rgba(108,71,255,.1); border: 1px solid rgba(108,71,255,.18);
    border-radius: 6px; padding: 3px 10px; font-size: .73rem; color: var(--violet2); font-weight: 500;
  }

  /* PRICING */
  .sd-v2 #pricing { position: relative; overflow: hidden; }
  .sd-v2 #pricing::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(108,71,255,.07), transparent 70%);
  }
  .sd-v2 .pricing-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 16px; margin-top: 56px; align-items: start;
    position: relative; z-index: 1;
  }
  .sd-v2 .pricing-card {
    background: var(--card-bg);
    border: 1px solid var(--border2);
    border-radius: 22px; padding: 36px; position: relative; transition: all .3s;
  }
  .sd-v2 .pricing-card:hover { transform: translateY(-4px); border-color: rgba(108,71,255,.25); }
  .sd-v2 .pricing-card.popular {
    background: linear-gradient(160deg, rgba(108,71,255,.12) 0%, rgba(37,99,235,.06) 100%);
    border: 1px solid rgba(108,71,255,.4);
    box-shadow: 0 0 0 1px rgba(108,71,255,.1), 0 0 60px rgba(108,71,255,.12);
  }
  .sd-v2 .popular-badge {
    position: absolute; top: -13px; left: 50%; transform: translateX(-50%);
    background: linear-gradient(135deg, var(--violet), #4f35d8);
    border-radius: 100px; padding: 4px 16px;
    font-size: .72rem; font-weight: 700; color: #fff;
    white-space: nowrap; letter-spacing: .3px;
    box-shadow: 0 4px 12px rgba(108,71,255,.4);
  }
  .sd-v2 .pricing-plan {
    font-size: .73rem; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: var(--violet2); margin-bottom: 14px;
  }
  .sd-v2 .pricing-price {
    font-size: 2.8rem; font-weight: 800; letter-spacing: -2px;
    color: #fff; line-height: 1; margin-bottom: 4px;
  }
  .sd-v2 .pricing-price span { font-size: .9rem; font-weight: 400; color: var(--muted2); letter-spacing: 0; }
  .sd-v2 .pricing-desc { font-size: .85rem; color: var(--muted2); margin-bottom: 24px; line-height: 1.6; }
  .sd-v2 .pricing-sep { height: 1px; background: var(--border2); margin-bottom: 24px; }
  .sd-v2 .pricing-features { list-style: none; display: flex; flex-direction: column; gap: 11px; margin-bottom: 32px; }
  .sd-v2 .pricing-features li {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: .875rem; color: #a09cb8; line-height: 1.5;
  }
  .sd-v2 .pricing-features li .check {
    width: 17px; height: 17px; border-radius: 50%; flex-shrink: 0;
    background: rgba(108,71,255,.15); border: 1px solid rgba(108,71,255,.3);
    display: flex; align-items: center; justify-content: center;
    margin-top: 1px;
  }
  .sd-v2 .pricing-features li .check::after {
    content: '✓'; font-size: .6rem; color: var(--violet2); font-weight: 700;
  }
  .sd-v2 .pricing-card .btn { width: 100%; justify-content: center; }

  /* MATCHING */
  .sd-v2 #matching { position: relative; overflow: hidden; }
  .sd-v2 #matching::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(108,71,255,.06), transparent 70%);
  }
  .sd-v2 .match-scene {
    display: flex; align-items: center; justify-content: center;
    gap: 20px; margin-top: 72px; position: relative;
  }
  .sd-v2 .match-card {
    width: 204px; flex-shrink: 0;
    background: rgba(7,7,22,.97);
    border: 1px solid rgba(255,255,255,.08);
    border-radius: 18px; padding: 18px;
    box-shadow: 0 32px 64px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.07), 0 0 0 1px rgba(108,71,255,.08);
    position: relative; z-index: 2;
  }
  .sd-v2 .match-card.left-card {
    transform: perspective(700px) rotateY(14deg);
    animation: sd-v2-mfl 8s ease-in-out infinite;
  }
  .sd-v2 .match-card.right-card {
    transform: perspective(700px) rotateY(-14deg);
    animation: sd-v2-mfr 8s ease-in-out infinite;
  }
  @keyframes sd-v2-mfl { 0%,100%{transform:perspective(700px) rotateY(14deg) translateY(0)} 50%{transform:perspective(700px) rotateY(14deg) translateY(-14px)} }
  @keyframes sd-v2-mfr { 0%,100%{transform:perspective(700px) rotateY(-14deg) translateY(-8px)} 50%{transform:perspective(700px) rotateY(-14deg) translateY(6px)} }

  .sd-v2 .mc-label { font-size:.58rem;font-weight:700;letter-spacing:1.8px;text-transform:uppercase;color:var(--muted2);margin-bottom:12px; }
  .sd-v2 .mc-head { display:flex;align-items:center;gap:9px;margin-bottom:14px; }
  .sd-v2 .mc-av {
    width:33px;height:33px;border-radius:9px;flex-shrink:0;
    background:linear-gradient(135deg,var(--violet),var(--blue));
    display:flex;align-items:center;justify-content:center;
    font-size:.64rem;font-weight:800;color:#fff;
  }
  .sd-v2 .mc-av-esn { background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.08); }
  .sd-v2 .mc-name { font-size:.82rem;font-weight:700;color:#fff; }
  .sd-v2 .mc-sub  { font-size:.62rem;color:#4a4870; }

  .sd-v2 .mc-skills { display:flex;flex-direction:column;gap:5px; }
  .sd-v2 .mc-skill {
    padding:5px 9px;border-radius:6px;font-size:.74rem;font-weight:600;
    color:#2d2b4e;border:1px solid rgba(255,255,255,.03);
    background:transparent;transition:all .45s ease;
  }
  .sd-v2 .mc-skill.sm { color:var(--violet2);background:rgba(108,71,255,.15);border-color:rgba(108,71,255,.4);box-shadow:0 0 12px rgba(108,71,255,.2); }
  .sd-v2 .mc-skill.sn { color:rgba(239,68,68,.75);background:rgba(239,68,68,.09);border-color:rgba(239,68,68,.3); }

  .sd-v2 .mc-reqs { display:flex;flex-direction:column;gap:7px; }
  .sd-v2 .mc-req { display:flex;align-items:center;gap:7px;font-size:.74rem;font-weight:500;color:#2d2b4e;transition:color .45s; }
  .sd-v2 .mc-req-icon {
    width:17px;height:17px;border-radius:50%;flex-shrink:0;
    border:1px solid rgba(255,255,255,.07);font-size:.55rem;
    display:flex;align-items:center;justify-content:center;
    transition:all .45s;
  }
  .sd-v2 .mc-req.rm { color:#a78bfa; }
  .sd-v2 .mc-req.rm .mc-req-icon { background:rgba(108,71,255,.2);border-color:rgba(108,71,255,.5);color:var(--violet2); }
  .sd-v2 .mc-req.rn { color:rgba(239,68,68,.65); }
  .sd-v2 .mc-req.rn .mc-req-icon { background:rgba(239,68,68,.1);border-color:rgba(239,68,68,.4);color:rgba(239,68,68,.9); }

  .sd-v2 .match-engine {
    flex-shrink:0;width:210px;height:210px;
    position:relative;z-index:1;
  }
  .sd-v2 .engine-overlay {
    position:absolute;inset:0;
    display:flex;flex-direction:column;align-items:center;justify-content:center;
    pointer-events:none;
  }
  .sd-v2 .engine-score-val {
    font-size:2rem;font-weight:800;letter-spacing:-2px;line-height:1;
    background:linear-gradient(135deg,#fff,var(--violet3));
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
    min-width:80px;text-align:center;
  }
  .sd-v2 .engine-score-lbl { font-size:.58rem;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--muted2);margin-top:2px; }

  .sd-v2 .match-result {
    text-align:center;margin-top:32px;
    opacity:0;transform:scale(.94) translateY(8px);
    transition:all .5s cubic-bezier(.34,1.56,.64,1);
  }
  .sd-v2 .match-result.show { opacity:1;transform:scale(1) translateY(0); }
  .sd-v2 .match-result-inner {
    display:inline-flex;align-items:center;gap:10px;
    background:linear-gradient(135deg,rgba(16,185,129,.12),rgba(16,185,129,.06));
    border:1px solid rgba(16,185,129,.28);
    border-radius:100px;padding:10px 26px;
    font-size:.88rem;font-weight:600;color:#6ee7b7;
    box-shadow:0 0 32px rgba(16,185,129,.12);
  }

  .sd-v2 .match-pills { display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:48px; }
  .sd-v2 .match-pill {
    background:rgba(255,255,255,.03);border:1px solid var(--border2);
    border-radius:100px;padding:8px 18px;
    font-size:.82rem;color:#5a5474;font-weight:500;
    transition:all .3s;
  }
  .sd-v2 .match-pill:hover { color:#c4bde0;border-color:rgba(108,71,255,.25); }

  /* LIBRARY */
  .sd-v2 #library {
    position: relative;
    background: linear-gradient(180deg, transparent 0%, rgba(108,71,255,.04) 50%, transparent 100%);
  }
  .sd-v2 .library-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 72px;
    align-items: center;
    margin-top: 72px;
  }
  .sd-v2 .library-mockup-wrap { position: relative; }
  .sd-v2 .library-mockup-glow {
    position: absolute;
    width: 320px; height: 320px;
    background: radial-gradient(circle, rgba(108,71,255,.2), transparent 70%);
    filter: blur(50px); border-radius: 50%;
    top: -40px; left: -40px;
    pointer-events: none;
  }
  .sd-v2 .lib-shell {
    background: rgba(8,8,24,.97);
    border: 1px solid rgba(255,255,255,.08);
    border-radius: 20px;
    overflow: hidden;
    box-shadow:
      0 0 0 1px rgba(108,71,255,.12),
      0 32px 80px rgba(0,0,0,.65),
      0 0 60px rgba(108,71,255,.1),
      inset 0 1px 0 rgba(255,255,255,.06);
    position: relative; z-index: 1;
  }
  .sd-v2 .lib-topbar {
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255,255,255,.05);
    display: flex; align-items: center; gap: 12px;
    background: rgba(255,255,255,.02);
  }
  .sd-v2 .lib-search {
    flex: 1; display: flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,.05);
    border: 1px solid rgba(255,255,255,.08);
    border-radius: 8px;
    padding: 7px 12px;
  }
  .sd-v2 .lib-search-icon { font-size: .85rem; opacity: .5; }
  .sd-v2 .lib-search-text {
    font-size: .75rem; color: #7a75a0; flex: 1;
    white-space: nowrap; overflow: hidden;
  }
  .sd-v2 .lib-search-cursor {
    width: 1.5px; height: 13px;
    background: var(--violet2);
    display: inline-block;
    animation: sd-v2-blink-cursor .8s step-end infinite;
  }
  @keyframes sd-v2-blink-cursor { 0%,100%{opacity:1} 50%{opacity:0} }
  .sd-v2 .lib-count {
    font-size: .72rem; font-weight: 700;
    color: #5a5878; white-space: nowrap;
  }
  .sd-v2 .lib-count strong { color: var(--violet2); }

  .sd-v2 .lib-filters {
    display: flex; gap: 6px; align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid rgba(255,255,255,.04);
    overflow: hidden;
  }
  .sd-v2 .lib-chip {
    padding: 4px 11px; border-radius: 100px;
    font-size: .67rem; font-weight: 700;
    background: rgba(255,255,255,.05);
    color: #5a5878;
    border: 1px solid rgba(255,255,255,.07);
    white-space: nowrap;
    transition: all .25s;
  }
  .sd-v2 .lib-chip.active {
    background: rgba(108,71,255,.18);
    color: var(--violet2);
    border-color: rgba(108,71,255,.3);
  }

  .sd-v2 .lib-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    background: rgba(255,255,255,.04);
    padding: 12px;
    gap: 8px;
  }
  .sd-v2 .lib-card {
    background: rgba(255,255,255,.03);
    border: 1px solid rgba(255,255,255,.06);
    border-radius: 10px;
    padding: 12px 10px;
    display: flex; flex-direction: column; gap: 8px;
    transition: border-color .25s, transform .25s;
    cursor: default;
    opacity: 0; transform: translateY(8px);
  }
  .sd-v2 .lib-card.show { opacity: 1; transform: translateY(0); }
  .sd-v2 .lib-card:hover { border-color: rgba(108,71,255,.3); transform: translateY(-2px); }
  .sd-v2 .lib-card-top { display: flex; align-items: center; gap: 8px; }
  .sd-v2 .lib-avatar {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    font-size: .6rem; font-weight: 800; color: #fff; flex-shrink: 0;
  }
  .sd-v2 .lib-name { font-size: .72rem; font-weight: 700; color: #e0dbff; line-height: 1.2; }
  .sd-v2 .lib-role { font-size: .62rem; color: #5a5878; }
  .sd-v2 .lib-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border-radius: 100px;
    font-size: .6rem; font-weight: 700;
  }
  .sd-v2 .lib-badge-dot { width: 5px; height: 5px; border-radius: 50%; }
  .sd-v2 .lib-badge.dispo {
    background: rgba(16,185,129,.1); color: #10b981;
    border: 1px solid rgba(16,185,129,.2);
  }
  .sd-v2 .lib-badge.dispo .lib-badge-dot { background: #10b981; }
  .sd-v2 .lib-badge.indispo {
    background: rgba(239,68,68,.08); color: #f87171;
    border: 1px solid rgba(239,68,68,.15);
  }
  .sd-v2 .lib-badge.indispo .lib-badge-dot { background: #f87171; }
  .sd-v2 .lib-badge.bientot {
    background: rgba(245,158,11,.08); color: #fbbf24;
    border: 1px solid rgba(245,158,11,.15);
  }
  .sd-v2 .lib-badge.bientot .lib-badge-dot { background: #fbbf24; }
  .sd-v2 .lib-skills { display: flex; flex-wrap: wrap; gap: 3px; }
  .sd-v2 .lib-skill {
    padding: 2px 6px; border-radius: 3px;
    font-size: .58rem; font-weight: 600;
    background: rgba(108,71,255,.1);
    color: #a78bfa;
  }

  .sd-v2 .lib-features { display: flex; flex-direction: column; gap: 28px; margin-top: 40px; }
  .sd-v2 .lib-feature {
    display: flex; gap: 18px; align-items: flex-start;
    opacity: 0; transform: translateX(-12px);
    transition: opacity .5s ease, transform .5s ease;
  }
  .sd-v2 .lib-feature.visible { opacity: 1; transform: translateX(0); }
  .sd-v2 .lib-feature-icon {
    width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
    background: linear-gradient(135deg, rgba(108,71,255,.18), rgba(37,99,235,.1));
    border: 1px solid rgba(108,71,255,.2);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.1rem;
  }
  .sd-v2 .lib-feature h4 { font-size: .95rem; font-weight: 700; margin-bottom: 5px; color: #fff; }
  .sd-v2 .lib-feature p { font-size: .875rem; color: #7a75a0; }

  /* CTA FINAL */
  .sd-v2 #cta { position: relative; overflow: hidden; }
  .sd-v2 #cta::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 70% at 50% 50%, rgba(108,71,255,.14), transparent 70%);
  }
  .sd-v2 #cta::after {
    content: ''; position: absolute; inset: 0;
    background-image: radial-gradient(rgba(108,71,255,.25) 1px, transparent 1px);
    background-size: 28px 28px;
    mask-image: radial-gradient(ellipse 70% 80% at 50% 50%, black, transparent);
    -webkit-mask-image: radial-gradient(ellipse 70% 80% at 50% 50%, black, transparent);
    opacity: .5;
  }
  .sd-v2 .cta-inner {
    position: relative; z-index: 1;
    text-align: center; max-width: 640px; margin: 0 auto;
  }
  .sd-v2 .cta-badge {
    display: inline-flex; align-items: center; gap: 8px;
    border: 1px solid var(--border);
    border-radius: 100px; padding: 5px 14px;
    font-size: .75rem; font-weight: 600; color: var(--muted2);
    margin-bottom: 28px; letter-spacing: .5px;
  }
  .sd-v2 .cta-inner h2 { margin-bottom: 18px; }
  .sd-v2 .cta-inner p { font-size: 1rem; margin: 0 auto 40px; max-width: 460px; }
  .sd-v2 .cta-buttons { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

  /* FOOTER */
  .sd-v2 footer {
    padding: 64px 0 40px;
    border-top: 1px solid var(--border);
    position: relative; z-index: 2;
  }
  .sd-v2 .footer-inner {
    display: flex; justify-content: space-between;
    align-items: flex-start; gap: 48px; flex-wrap: wrap;
  }
  .sd-v2 .footer-brand { max-width: 240px; }
  .sd-v2 .footer-brand p { font-size: .84rem; color: var(--muted2); margin-top: 14px; line-height: 1.7; }
  .sd-v2 .footer-col h4 {
    font-size: .78rem; font-weight: 700; color: #fff;
    letter-spacing: 1px; text-transform: uppercase;
    margin-bottom: 18px;
  }
  .sd-v2 .footer-col ul { list-style: none; display: flex; flex-direction: column; gap: 11px; }
  .sd-v2 .footer-col a {
    font-size: .84rem; color: var(--muted2); text-decoration: none; transition: color .2s;
  }
  .sd-v2 .footer-col a:hover { color: #fff; }
  .sd-v2 .footer-bottom {
    margin-top: 52px; padding-top: 24px;
    border-top: 1px solid var(--border);
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; gap: 12px;
  }
  .sd-v2 .footer-bottom p { font-size: .8rem; color: var(--muted2); }

  /* Fade-up */
  .sd-v2 .fade-up { opacity: 0; transform: translateY(28px); transition: opacity .65s ease, transform .65s ease; }
  .sd-v2 .fade-up.visible { opacity: 1; transform: translateY(0); }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .sd-v2 .hero-mockup { display: none; }
    .sd-v2 nav .nav-links { display: none; }
    .sd-v2 nav .nav-actions { display: none; }
    .sd-v2 .mobile-menu-btn { display: flex; align-items: center; justify-content: center; }
    .sd-v2 .steps-container::before { display: none; }
    .sd-v2 .steps-container { grid-template-columns: 1fr; gap: 40px; }
    .sd-v2 .match-scene { flex-direction: column; gap: 24px; }
    .sd-v2 .match-card.left-card, .sd-v2 .match-card.right-card { transform: none !important; animation: none !important; }
    .sd-v2 .match-engine { width: 160px; height: 160px; }
    .sd-v2 .library-layout { grid-template-columns: 1fr; gap: 48px; }
    .sd-v2 .library-mockup-wrap { display: none; }
    .sd-v2 section { padding: 60px 0; }
    .sd-v2 .pricing-grid { grid-template-columns: 1fr; max-width: 400px; margin-left: auto; margin-right: auto; }
    .sd-v2 .footer-inner { flex-direction: column; gap: 32px; }
    .sd-v2 .footer-bottom { flex-direction: column; text-align: center; }
    .sd-v2 .cta-buttons { flex-direction: column; align-items: center; }
    .sd-v2 .cta-buttons .btn { width: 100%; max-width: 320px; justify-content: center; }
    .sd-v2 .stats-inner { flex-direction: column; align-items: center; padding: 30px 0; }
    .sd-v2 .stat:not(:last-child)::after { display: none; }
    .sd-v2 .hero-ctas { flex-direction: column; }
    .sd-v2 .hero-ctas .btn { width: 100%; justify-content: center; }
  }

  @media (max-width: 480px) {
    .sd-v2 .container { padding: 0 16px; }
    .sd-v2 .problem-grid, .sd-v2 .features-grid, .sd-v2 .cases-grid { grid-template-columns: 1fr; }
    .sd-v2 .problem-card, .sd-v2 .feature-card, .sd-v2 .case-card { padding: 20px; }
  }

  /* LOGIN SLIDE-IN PANEL (preserved from original) */
  .sd-login-overlay {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(0,0,0,0); backdrop-filter: blur(0);
    pointer-events: none; transition: background .4s ease, backdrop-filter .4s ease;
  }
  .sd-login-overlay-visible {
    background: rgba(0,0,0,.6); backdrop-filter: blur(6px);
    pointer-events: auto;
  }
  .sd-login-panel {
    position: fixed; top: 0; right: 0; bottom: 0; z-index: 201;
    width: 420px; max-width: 100vw;
    background: #0a0a1e; border-left: 1px solid rgba(108,71,255,.3);
    box-shadow: -20px 0 80px rgba(0,0,0,.7), 0 0 40px rgba(108,71,255,.15);
    transform: translateX(100%);
    transition: transform .4s cubic-bezier(.4,0,.2,1);
    display: flex; flex-direction: column;
    overflow-y: auto;
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
  .sd-login-panel-open { transform: translateX(0); }
  .sd-login-close {
    position: absolute; top: 20px; right: 20px;
    background: rgba(108,71,255,.15); border: 1px solid rgba(108,71,255,.3);
    color: #a78bfa; width: 36px; height: 36px; border-radius: 50%;
    font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all .2s;
  }
  .sd-login-close:hover { background: rgba(108,71,255,.3); color: #fff; }
  .sd-login-panel-inner {
    flex: 1; display: flex; flex-direction: column; justify-content: center;
    padding: 60px 40px;
  }
  .sd-login-panel-header { text-align: center; margin-bottom: 36px; }
  .sd-login-panel-header h2 {
    font-size: 1.6rem; font-weight: 700; color: #fff; margin: 20px 0 8px;
  }
  .sd-login-panel-header p {
    font-size: .9rem; color: #6b6a8a;
  }
  .sd-login-form { display: flex; flex-direction: column; gap: 20px; }
  .sd-login-field { display: flex; flex-direction: column; gap: 6px; }
  .sd-login-field label { font-size: .85rem; font-weight: 600; color: #c4bde0; }
  .sd-login-field input {
    background: rgba(255,255,255,.06); border: 1px solid rgba(108,71,255,.25);
    border-radius: 10px; padding: 12px 16px; color: #fff; font-size: .95rem;
    outline: none; transition: border-color .2s;
    font-family: inherit;
  }
  .sd-login-field input::placeholder { color: #4a4870; }
  .sd-login-field input:focus { border-color: #6c47ff; box-shadow: 0 0 0 3px rgba(108,71,255,.15); }
  .sd-login-field input.sd-input-error { border-color: #ef4444; }
  .sd-field-error { font-size: .75rem; color: #ef4444; }
  .sd-login-footer-text {
    text-align: center; margin-top: 28px; font-size: .9rem; color: #6b6a8a;
  }
  .sd-link-btn {
    background: none; border: none; color: #a78bfa; font-weight: 600; cursor: pointer;
    font-size: .9rem; text-decoration: underline; padding: 0;
    font-family: inherit;
  }
  .sd-link-btn:hover { color: #c4b5fd; }
  .sd-login-panel .btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 20px; border-radius: 10px;
    font-size: .9rem; font-weight: 600;
    cursor: pointer; transition: all .22s;
    text-decoration: none; border: none;
    font-family: inherit;
  }
  .sd-login-panel .btn-primary {
    background: linear-gradient(135deg, #6c47ff 0%, #4f35d8 100%);
    color: #fff;
    box-shadow: 0 0 0 1px rgba(108,71,255,.4), 0 4px 24px rgba(108,71,255,.35);
  }
  .sd-login-panel .btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px rgba(108,71,255,.6), 0 8px 32px rgba(108,71,255,.5);
  }
  .sd-login-panel .btn-primary:disabled { opacity: .65; cursor: not-allowed; }
  @keyframes sd-spin-anim { to { transform: rotate(360deg); } }
  .sd-spin { animation: sd-spin-anim 1s linear infinite; }

  @media (max-width: 480px) {
    .sd-login-panel { width: 100vw; }
    .sd-login-panel-inner { padding: 60px 24px; }
  }

  /* ── Méga-menu produit ── */
  .sd-v2 .nav-mega-wrap { position: relative; }
  .sd-v2 .nav-mega-trigger {
    display: inline-flex; align-items: center; gap: 6px;
    background: none; border: none; cursor: pointer; font-family: inherit;
    font-size: 14.5px; font-weight: 500; color: var(--muted2);
    padding: 0; transition: color .15s;
  }
  .sd-v2 .nav-mega-trigger:hover,
  .sd-v2 .nav-mega-wrap.open .nav-mega-trigger { color: var(--text); }
  .sd-v2 .nav-mega-caret { transition: transform .2s; }
  .sd-v2 .nav-mega-wrap.open .nav-mega-caret { transform: rotate(180deg); }

  .sd-v2 .nav-mega {
    position: absolute; top: calc(100% + 14px); left: 50%;
    transform: translateX(-50%) translateY(-6px);
    width: min(860px, calc(100vw - 48px));
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 8px 30px;
    padding: 26px 28px 20px;
    background: rgba(10,10,28,.96);
    border: 1px solid var(--border);
    border-radius: 18px;
    box-shadow: 0 30px 80px -20px rgba(0,0,0,.7), 0 0 0 1px rgba(255,255,255,.03) inset;
    backdrop-filter: blur(20px);
    opacity: 0; visibility: hidden; pointer-events: none;
    transition: opacity .18s ease, transform .18s ease, visibility .18s;
    z-index: 200;
  }
  .sd-v2 .nav-mega-wrap.open .nav-mega {
    opacity: 1; visibility: visible; pointer-events: auto;
    transform: translateX(-50%) translateY(0);
  }
  /* Pont invisible : sans lui, le curseur "tombe" entre l'onglet et le panneau. */
  .sd-v2 .nav-mega-wrap::after {
    content: ''; position: absolute; top: 100%; left: 0; right: 0; height: 16px;
  }

  .sd-v2 .nav-mega-col { display: flex; flex-direction: column; gap: 2px; }
  .sd-v2 .nav-mega-head {
    font-size: 11px; font-weight: 800; letter-spacing: 1.1px; text-transform: uppercase;
    color: var(--muted2); margin-bottom: 10px; padding-left: 10px;
  }
  .sd-v2 .nav-mega a {
    display: flex; flex-direction: column; gap: 3px;
    padding: 9px 10px; border-radius: 10px;
    text-decoration: none; transition: background .14s;
  }
  .sd-v2 .nav-mega a:hover { background: var(--glass); }
  .sd-v2 .nmi-t {
    font-size: 14px; font-weight: 700; color: var(--text);
    display: inline-flex; align-items: center; gap: 8px;
  }
  .sd-v2 .nmi-d { font-size: 12.5px; color: var(--muted2); line-height: 1.45; }
  .sd-v2 .nmi-badge {
    font-size: 9.5px; font-weight: 800; letter-spacing: .5px; text-transform: uppercase;
    padding: 2px 7px; border-radius: 20px;
    background: rgba(108,71,255,.18); color: var(--violet3);
  }
  .sd-v2 .nav-mega-foot {
    grid-column: 1 / -1; margin-top: 14px; padding-top: 16px;
    border-top: 1px solid var(--border2);
  }
  .sd-v2 .nav-mega-foot a {
    padding: 0; font-size: 13.5px; font-weight: 700; color: var(--violet3);
  }
  .sd-v2 .nav-mega-foot a:hover { background: none; color: var(--text); }

  /* Sections titrées du menu mobile */
  .sd-v2 .mobile-menu-links .mm-head {
    font-size: 11px; font-weight: 800; letter-spacing: 1.1px; text-transform: uppercase;
    color: var(--muted2); margin: 18px 0 4px;
  }
  .sd-v2 .mobile-menu-links .mm-head:first-child { margin-top: 0; }
  .sd-v2 .mobile-menu-links .mm-sep {
    height: 1px; background: var(--border2); margin: 20px 0 6px;
  }

  /* Même seuil que .nav-links : sinon l'onglet « Produit » resterait visible
     alors que son panneau est masqué, et le clic ne ferait rien. */
  @media (max-width: 900px) {
    .sd-v2 .nav-mega { display: none; }
  }

  /* ── CRM — comptes clients & pilotage ── */
  .sd-v2 #crm { position: relative; padding: 120px 0; }
  .sd-v2 .crm-split {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 56px; align-items: center; margin-top: 64px;
  }
  .sd-v2 .crm-visual {
    background: var(--card-bg); border: 1px solid var(--border);
    border-radius: 20px; padding: 22px;
    backdrop-filter: blur(12px);
  }
  .sd-v2 .crm-tree { display: flex; flex-direction: column; gap: 10px; }
  .sd-v2 .crm-node {
    background: rgba(255,255,255,.03); border: 1px solid var(--border2);
    border-radius: 12px; padding: 12px 14px;
    display: flex; align-items: center; gap: 12px;
  }
  .sd-v2 .crm-node.lvl1 { margin-left: 22px; }
  .sd-v2 .crm-node.lvl2 { margin-left: 44px; }
  .sd-v2 .crm-ic {
    width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
    background: rgba(108,71,255,.16); color: var(--violet3);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800;
  }
  .sd-v2 .crm-nm { font-size: 14px; font-weight: 700; color: var(--text); }
  .sd-v2 .crm-sub { font-size: 12px; color: var(--muted2); margin-top: 2px; }
  .sd-v2 .crm-pill {
    margin-left: auto; flex-shrink: 0;
    font-size: 10.5px; font-weight: 800; padding: 4px 11px; border-radius: 20px;
    background: rgba(74,222,128,.14); color: #4ade80;
  }
  .sd-v2 .crm-pill.warn { background: rgba(250,204,21,.14); color: #facc15; }
  .sd-v2 .crm-alert {
    margin-top: 18px; padding: 14px 16px; border-radius: 12px;
    background: linear-gradient(135deg, rgba(250,204,21,.10), rgba(108,71,255,.08));
    border: 1px solid rgba(250,204,21,.28);
    font-size: 13px; font-weight: 600; color: #fde68a;
  }
  .sd-v2 .crm-points { display: flex; flex-direction: column; gap: 30px; }
  .sd-v2 .crm-point { display: flex; gap: 16px; align-items: flex-start; }
  .sd-v2 .crm-point-ic {
    font-size: 22px; flex-shrink: 0;
    width: 46px; height: 46px; border-radius: 13px;
    background: var(--glass); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
  }
  .sd-v2 .crm-point h3 {
    font-size: 17px; font-weight: 700; color: var(--text);
    margin-bottom: 6px; letter-spacing: -.3px;
  }
  .sd-v2 .crm-point p { font-size: 14.5px; color: var(--muted2); line-height: 1.65; }

  @media (max-width: 900px) {
    .sd-v2 .crm-split { grid-template-columns: 1fr; gap: 40px; margin-top: 44px; }
    .sd-v2 #crm { padding: 84px 0; }
    .sd-v2 .crm-node.lvl1 { margin-left: 14px; }
    .sd-v2 .crm-node.lvl2 { margin-left: 28px; }
  }
`;

export default LandingPage;
